import React from 'react'
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom'; // for adding DOM elements such as vehiclePopup
import Events from './../common/events';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl'; // https://docs.mapbox.com/mapbox-gl-js/guides/install/#excluding-mapbox-gl-js-explicitly-from-transpilation
import VehiclePopup from './vehiclePopup';
import TelematicsHelper from './telematicsHelper'
import {MARKER_STATES, RUNTIME_MAP_FEATURES, EVENT_CATEGORY_NAMES, EVENT_CATEGORY_IDS} from './mapboxMapDashboardConstants';
import BackgroundRefresher from '../common/backgroundRefresher';
import ColorUtil from '../common/ColorUtil';
import GeofenceEditor, {GeofenceEditorBackground} from './GeofenceEditor/';
import MapboxHelper, { GEOFENCE_LAYER_IDS } from './mapboxHelperDashboard';
import { ACTIVE_MENU_ITEM_IDS } from 'APP_ROOT/data/layoutTemplateModularFullscreen/duck';
import HistoryPopup  from './mapboxRoutePopup';
import MapboxToggleTrafficControl from './mapboxExtendedControls/mapboxToggleTrafficControl'
import MapboxToggleSatelliteControl from './mapboxExtendedControls/mapboxToggleSatelliteControl';
import MapboxToggleFollowSelectedRavenControl from './mapboxExtendedControls/mapboxToggleFollowSelectedRavenControl';
import MapboxToggleFullscreenControl from './mapboxExtendedControls/mapboxToggleFullscreenControl';
import MapboxToggleRavenTrailsControl from './mapboxExtendedControls/mapboxToggleRavenTrailsControl';
import { focusSearchInput, MAP_FULLSCREEN_EVENT } from '../common/keyboardShortcuts';
import ResizeThrottler from 'APP_ROOT/components/ResizeThrottler';
import { strings } from './../../localization';
import fscreen from 'fscreen';
import { min } from 'moment';

// Asset clustering constants
const ZOOM_RADIUS_MAPPING = {
    18: [0.01, 0.01], // 10 meters
    17: [0.05, 0.1],
    16: [0.1, 0.2],
    15: [0.2, 1],
    14: [1, 1.5],
    13: [1.5, 2.5],
    12: [2.5, 3.5],
    11: [3.5, 5],
    10: [5, 7],
    9: [7, 10],
    8: [10, 20],
    7: [20, 50],
    6: [50, 80],
    5: [80, 100],
    4: [100, 500],
    3: [500, 800],
    2: [800, 1000],
    1: [1000, 2000],
}

const ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST = 5; // if there are more than this number of points in a cluster, show title + an arrow (instead of a list of points' names)
const ASSETS_CLUSTERS_FONT_SIZE = 12;
const ASSETS_CLUSTERS_LIST_WIDTH = 200;
const ASSETS_CLUSTERS_LIST_BORDER_WIDTH = 2;
const ASSETS_CLUSTERS_BASE_RADIUS = 5;
const ASSETS_CLUSTERS_SINGLE_ASSET_PADDING = 5;
const ASSETS_CLUSTERS_LIST_PADDING = 5;
const ASSETS_CLUSTERS_STEM_LENGTH = 27;
const ASSETS_CLUSTERS_STEM_LONG_LENGTH = 85; // For left/right side cluster (to avoid covering a selected PIN)
const ASSETS_CLUSTERS_LIST_EXPAND_ANIMATION_DURATION = .3; // seconds
const ASSETS_CLUSTERS_LIST_COLLAPSE_ANIMATION_DURATION = .2; // seconds. This should be less than ASSETS_CLUSTERS_LIST_EXPAND_ANIMATION_DURATION to close the list before the wrapping div is collapsed
const ASSETS_CLUSTERS_COLORS = {
    SELECTED_CLUSTER_BACKGROUND: '#0765B3',
    DEFAULT_CLUSTER_BACKGROUND: '#62A6DE',
    CLUSTER_BORDER: '#7EC4FF',
    SELECTED_ASSET_BACKGROUND: '#7EC4FF',
    SELECTED_ASSET_COLOR: '#000',
    DEFAULT_ASSET_BACKGROUND: 'transparent',
    DEFAULT_ASSET_COLOR: 'white',
}
const ASSETS_CLUSTERS_CLASSES = {
    CLUSTER_MARKER: 'cluster-marker',
    CLUSTER_WRAPPER: 'outer-wrapper',
    LIST_WRAPPER: 'inner-wrapper',
    NORMAL_LIST_BACKGROUND_RECT: 'cluster-bg',
    EXPANDABLE_LIST_BACKGROUND_RECT: 'cluster-bg-expandable',
    ASSET_BACKGROUND_RECT: 'cluster-asset-list-item-bg',
    ASSET_TEXT: 'cluster-asset-list-item',
    SELECTED: 'selected',
    CONTAINS_SELECTED_ASSET: 'contains-selected-asset',
}
const ASSETS_CLUSTERS_ANCHORS = {
    BOTTOM: 'bottom',
    TOP: 'top',
    LEFT: 'left',
    RIGHT: 'right',
}
const ASSETS_CLUSTERS_POSITION = {
    LEFT: 'left',
    RIGHT: 'right',
    ABOVE: 'above',
    BELOW: 'below',
    ALIGNED: 'aligned',
}


const OPTIMIZE_GPS_DATA = true;
const MAPBOX_DEFAULT_DARK_STYLE_ID = "cjs6ev22e0j5u1fnu2z4eg3mu";
const MAPBOX_DEFAULT_LIGHT_STYLE_ID = "cjrtvghfhkgwa2so1ig6w6h0a";
const MAPBOX_SATELLITE_STYLE_ID = "cjve3u9xg6wey1gmpnmnip36t";
const TIMEOUT_DURATION_TO_TOGGLE_TRAFFIC_OFF = 30000; // five minutes (1000 * 60 * 5)
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;


const ENHANCED_VIDEO_ACCESS_ROUTE_HIGHLIGHT_THROTTLE_DELAY = 41.6666667; // Adjust for performance optimization.  E.g.s 41.6666667 (24/s), 83.3333333 (12/s), 166.66667 (6/s), 333.333333 (3/s)
const OPTIMIZE_GEOMETRIES_VIA_TOLERANCE = true;
const LOW_DENSITY_MONITORS_MAGIC_SCALE_FACTOR_NUMERATOR = 1.38888888888; // rough notes: 1/1.38888888888 is 0.72
const LAYER_IDS = {
    GEOFENCES: {
        POLYGONS: 'klashwerks_all_geofences_layer',
        LABELS: 'klashwerks_all_geofence_labels_layer',
        SELECTED_OUTLINE: 'klashwerks_selected_geofence_outline_layer'
    },
};

// Image "RUNTIME_ONLY_VEHICLE_MARKER-bc26abcdd90a46a2b9edebb3bdb2ae8a-problem" could not be loaded. Please make sure you have added the image with map.addImage() or a "sprite" property in your style. You can provide missing images by listening for the "styleimagemissing" map event.

const SOURCE_RAVEN_VEHICLE_POINTS_NAME = "klashwerks_all_ravens_vehicles_points";
const SOURCE_SELECTED_RAVEN_VEHICLE_POINTS_NAME = "klashwerks_selected_raven_vehicles_points";
const SOURCE_RAVEN_EVENTS_NAME = "klashwerks_all_ravens_events_points";
const SOURCE_RAVEN_ROUTE_NAME = "klashwerks_all_ravens_route_lines";
const SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME = "klashwerks_enhanced_video_access_route_highlight_line";
const SOURCE_GEOFENCE_NAME = "klashwerks_all_geofences";
const SOURCE_GEOFENCE_LABEL_NAME = "klashwerks_all_geofence_labels";
const SOURCE_ASSETS_NAME = "klashwerks_assets_points";
const LAYER_RAVEN_VEHICLE_POINTS_NAME = "klashwerks_all_ravens_vehicles_points_layer";
const LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME = "klashwerks_all_ravens_vehicles_points_selected_layer";
const LAYER_RAVEN_EVENTS_NAME = "klashwerks_all_ravens_events_points_layer"
const LAYER_SELECTED_RAVEN_EVENTS_NAME = "klashwerks_all_ravens_events_points_selected_layer"
const LAYER_RAVEN_ROUTE_NAME = "klashwerks_all_ravens_route_lines_layer";
const LAYER_ENHANCED_VIDEO_ACCESS_ROUTE_NAME = "klashwerks_enhanced_video_access_route_highlight_layer";
const LAYER_ASSETS_NAME = "klashwerks_assets_points_layer";
const LAYER_CLUSTERS_ASSETS_NAME = "klashwerks_assets_points_clusters_layer";

export default class DashboardMap extends React.Component {

    constructor(props) {

        super(props);

        this.mapLoaded = false;

        this.mapboxUserHasMovedMap = false;

        this.mapboxVehiclePopupIsStatic = false;
        this.mapboxVehiclePopupCameraIndex = 0;

        this.mapboxBoundsSetToFitAllRavensRoutes = false;
        this.sourceEnhancedVideoAccessRoute = undefined; // quick reference for rendered SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME
        this.sourceEnhancedVideoAccessRouteUpdateTimerId = undefined; // used by throttle function


        this.mapSelectedRavenUuid = null;
        this.mapHoveredRavenUuid = null;

        this.propsTotalsByRavenUuid = {};

        this.mapboxBoundsForAllRavensRoutes = new mapboxgl.LngLatBounds();

        this.mapboxGeolocateControl = null;

        // mapbox controls default states
        this.mapboxControlShouldDisplayTrafficByDefault = false;
        this.mapboxControlShouldDisplaySatelliteByDefault = false;
        this.mapboxToggleTrafficControl = null;
        this.mapboxControlIsFollowingSelectedRaven = false;
        this.mapboxToggleFollowSelectedRavenControl = null;
        this.mapboxControlShouldFullscreen = false;
        this.mapboxToggleFullscreenControl = null;
        this.mapboxToggleRavenTrailsControl = null;
        this.mapboxToggleSatelliteControl = null;

        this.geofenceLayersVisible = true;

        this.mapboxDataLastUpdate = Date.now();

        this.mapboxToggleTrafficOnAppPauseTimeout = null;

        // objects for caching and keeping track of assets clusters marker objects (for performance)
        this.assetClustersMarkers = {};
        this.assetClustersMarkersOnScreen = {};
        this.assetsClustersForMaxZoom = [];

        this.mapDataModel = {
            ravenData: {}
        };
        this.state = {};

        document.addEventListener(Events.PAUSE_DFAST, this.onAppPauseEvent); // use this event to toggle traffic off after TIMEOUT_DURATION_TO_TOGGLE_TRAFFIC_OFF
        document.addEventListener(Events.UNPAUSE_DFAST, this.onAppResumeEvent); // use this event to clear TIMEOUT_DURATION_TO_TOGGLE_TRAFFIC_OFF timeout to toggle traffic off
        document.addEventListener(Events.ENHANCED_VIDEO_ACCESS_ROUTE_HIGHLIGHT_REQUEST, this.throttledEnhancedVideoAccessRouteHighlightRequestHandler);
    }

    onKeyDown = (event) => {
        if (event.target) {
            switch (event.target.tagName) {
                case "INPUT":
                case "TEXTAREA":
                    return; // do nothing, do not interfere with keydowns on text input elements
                default:
                    break;
            }
        }
        // Handle Ctrl+F / Cmd+F: open sidebar search
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            event.preventDefault();
            event.stopPropagation();
            focusSearchInput(this.props.setMainModuleGroupNavigation);
            return;
        }

        // Don't intercept other Ctrl/Cmd key combos
        if (event.ctrlKey || event.metaKey) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        switch (event.key) {
            case 'Escape': // likely handled by the browser already, if native fullscreen supported
                if (this.mapboxControlShouldFullscreen) {
                    this.mapboxToggleFullscreenControlCallback(!this.mapboxControlShouldFullscreen);
                }
                break;
            case 'f':
                if (!event.ctrlKey && !event.metaKey && !this.mapboxVehiclePopupIsEditingMessage) {
                    if (!this.mapboxControlShouldFullscreen) {
                        this.mapboxToggleFullscreenControlCallback(!this.mapboxControlShouldFullscreen);
                    }
                }
                break;
            default:
                break;
        }
    }

    onMapFullscreenRequest = () => {
        if (!this.mapboxControlShouldFullscreen && this.mapboxToggleFullscreenControl) {
            this.mapboxToggleFullscreenControlCallback(true);
        }
    }

    componentWillUnmount () {

        document.removeEventListener(Events.SELECT_EVENT, this.onSelectEvent);
        document.removeEventListener(Events.PAUSE_DFAST, this.onAppPauseEvent); // use this event to toggle traffic off after TIMEOUT_DURATION_TO_TOGGLE_TRAFFIC_OFF
        document.removeEventListener(Events.UNPAUSE_DFAST, this.onAppResumeEvent); // use this event to clear TIMEOUT_DURATION_TO_TOGGLE_TRAFFIC_OFF timeout to toggle traffic off
        document.removeEventListener(Events.ENHANCED_VIDEO_ACCESS_ROUTE_HIGHLIGHT_REQUEST, this.throttledEnhancedVideoAccessRouteHighlightRequestHandler);
        const mapContainer = document.getElementById(this.props.mapId);
        if (mapContainer) {
            mapContainer.removeEventListener("keydown", this.onKeyDown, false);
        }
        fscreen.removeEventListener('fullscreenchange', this.onFullscreenChange, false);
        document.removeEventListener(MAP_FULLSCREEN_EVENT, this.onMapFullscreenRequest);
        if (this.mapboxControlShouldFullscreen) {
            this.props.setLayoutFullscreen(false); 
            fscreen.exitFullscreen();
        }
    }

    componentDidMount() {
        const mapContainer = document.getElementById(this.props.mapId);
        if (mapContainer) {
            mapContainer.addEventListener("keydown", this.onKeyDown, false);
        }
        fscreen.addEventListener('fullscreenchange', this.onFullscreenChange, false);
        document.addEventListener(MAP_FULLSCREEN_EVENT, this.onMapFullscreenRequest);

        this.props.loadActiveGeofences(this.props.stage).then(() => {

            if(this.props.canAccessAssets) {
                this.props.loadAllAssets(this.props.stage)
                .catch((err) => {
                    console.error("Error loading assets", err);
                    // An error is shown in the assets list (resourceGroup.js)
                });

                this.props.loadAllBeacons(this.props.stage)
                .catch((err) => {
                    console.error("Error loading beacons", err);
                    // An error is shown in the assets list (resourceGroup.js)
                });
            }
        });

        document.addEventListener(Events.SELECT_EVENT, this.onSelectEvent);

        this.map = new mapboxgl.Map({
            container: this.props.mapId,
            style: this.props.theme !== 'light' ? 'mapbox://styles/dan-in-ottawa/' + MAPBOX_DEFAULT_DARK_STYLE_ID : 'mapbox://styles/dan-in-ottawa/' + MAPBOX_DEFAULT_LIGHT_STYLE_ID,
            //center: [0, 0],
            // Center of North America
            center: [-99.954143, 41.9735726],
            zoom: 4,
            maxZoom: 18
        });

        this.map.on('load', () => {

            this.mapLoaded = true;

            // this.currentClusterRadius = this.calculateClusterRadius(this.map.getZoom());

            // Set up sources and layers

            this.addEmptySourcesAndLayers(this.map);

            this.map.addControl(new mapboxgl.NavigationControl());

            this.mapboxGeolocateControl = new mapboxgl.GeolocateControl({
                positionOptions: {
                    enableHighAccuracy: true
                },
                trackUserLocation: true
            });
            this.map.addControl(this.mapboxGeolocateControl);
            if (this.props.features.traffic === true) {
                this.mapboxAddToggleTrafficControl();
            }

            if (this.props.features.satellite === true) {
                this.mapboxAddToggleSatelliteControl();
            }


            this.mapboxAddToggleFollowSelectedRavenControl();

            this.updateMapboxGeofences();

            this.setMapSourceFromRavens();

            this.mapboxAddToggleRavenTrailsControl();
            this.mapboxAddToggleFullscreenControl();

            // Fired when a pointing device (usually a mouse) is pressed and released at the same point on the map.
            this.map.on("click", this.onMapClick);
            // Fired just after the map completes a transition from one view to another, as the result of either user interaction or methods such as Map#jumpTo.
            this.map.on("moveend", this.onMapMoveEnd);
            this.map.on('dragstart', this.onMapDragStart);
            this.map.on('mousemove', "klashwerks_all_geofences_layer", this.onMapMouseMoveGeofenceLayer);
            this.map.on('mouseleave', "klashwerks_all_geofences_layer", this.onMapMouseLeaveGeofenceLayer);
            this.map.on('zoom', this.updateClusters);

            const historicalStreamingEnabled  = this.props.features?.historical_streaming;  // same as line 337 components/pages/dashboard/EventMedia/index.js
            this.historyPopup = new HistoryPopup(
                historicalStreamingEnabled, // discovered this was hard-coded to "true", to quickly resolve RAV-3632 I am setting based on historical_streaming (see components/common/views.js)
                this.onLiveStreamAtTime
            );

            this.historyPopup.addToMap(this.map, LAYER_RAVEN_ROUTE_NAME, SOURCE_RAVEN_ROUTE_NAME);


            this.mapboxGeolocateControl.on('geolocate', (e) => { // https://docs.mapbox.com/mapbox-gl-js/api/#geolocatecontrol.event:geolocate
                //console.log("geolocate");
                if (this.mapboxControlIsFollowingSelectedRaven === true) {
                    this.mapboxControlIsFollowingSelectedRaven = false;
                    this.mapboxAddToggleFollowSelectedRavenControl();
                }
            });

            this.mapboxGeolocateControl.on('trackuserlocationstart', (e) => { // https://docs.mapbox.com/mapbox-gl-js/api/#geolocatecontrol.event:trackuserlocationstart
                //console.log("trackuserlocationstart");
                if (this.mapboxControlIsFollowingSelectedRaven === true) {
                    this.mapboxControlIsFollowingSelectedRaven = false;
                    this.mapboxAddToggleFollowSelectedRavenControl();
                }
            });

            var selectedRavenUuid = this.getSelectedRavenUuidFromProps();
            if (selectedRavenUuid) {
                this.mapSelectedRavenUuid = selectedRavenUuid;

                // Request the route info
                const isUserClick = false;
                // this.props.onSelectResource('vehicle', this.mapSelectedRavenUuid, isUserClick);
                this.setSelectedFeatureStateForRavenUuid(true, selectedRavenUuid);
            }
            const selectedassetUuid = this.getSelectedassetUuid();
            if (selectedassetUuid) this.setSelectedFeatureStateForAsset(true, selectedassetUuid);
        });

        
        // this.map.on('render', () => {
        //     if(this.mapLoaded)
        //         this.updateMarkersThrottled();
        // });
    }    

    onAppResumeEvent = (e) => {
        clearTimeout(this.mapboxToggleTrafficOnAppPauseTimeout);
    }

    onAppPauseEvent = (e) => {
        clearTimeout(this.mapboxToggleTrafficOnAppPauseTimeout);
        this.mapboxToggleTrafficOnAppPauseTimeout = setTimeout(() => {
            if (this.mapboxControlShouldDisplayTrafficByDefault) {
                //console.warn("Toggling traffic off due to web app pause event");
                this.mapboxToggleTrafficControl.toggleTrafficVisiblity();
            }
        }, TIMEOUT_DURATION_TO_TOGGLE_TRAFFIC_OFF);
    }

    onMapClick = (e) => {
        const { resourceType } = this.props.selectedResource;

        if (this.props.geofenceGeometryEditModeActive && resourceType === 'geofence') {
            return;
        }
        
        // set a relatively precise area to determine if a raven feature was clicked
        var boundingBoxAroundClickedPoint = []; // https://docs.mapbox.com/mapbox-gl-js/example/queryrenderedfeatures-around-point/
        var marginAroundBoundingBox = 5; // pixels
        boundingBoxAroundClickedPoint.push([e.point.x - marginAroundBoundingBox, e.point.y - marginAroundBoundingBox]); // set the southwestern corner of the bounding box which corresponds to a 5 PIXEL (implications vary greatly by zoom level) area around the clicked point
        boundingBoxAroundClickedPoint.push([e.point.x + marginAroundBoundingBox, e.point.y + marginAroundBoundingBox]); // set the northeastern corder of the bounding box which corresponds to a 5 pixel area around the clicked point

        var ravenRelatedLayers = [
            LAYER_ASSETS_NAME,
            LAYER_CLUSTERS_ASSETS_NAME,
            LAYER_RAVEN_ROUTE_NAME,
            LAYER_RAVEN_VEHICLE_POINTS_NAME, // we don't need to worry about "klashwerks_all_ravens_vehicles_points_selected_layer" (same points)
            LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME,
            LAYER_RAVEN_EVENTS_NAME // we don't need to worry about "klashwerks_all_ravens_events_points_selected_layer" (same points)
        ];

        var featuresForClickedArea = this.map.queryRenderedFeatures(boundingBoxAroundClickedPoint, {layers: ravenRelatedLayers});
        
        if (featuresForClickedArea.length > 0) { // check if user is clicking away from a raven feature (i.e., the user is attempting to clear the selection)

            var topmostFeature = featuresForClickedArea[0];

            // If it's a cluster
            if (topmostFeature.properties.cluster) {
                this.props.onSelectResource('asset', undefined);
                this.IdOfclusterImageClickedByUser = topmostFeature.properties.cluster_id;
                return;
            }

            var ravenUuidForTopmostFeature = topmostFeature.properties.ravenUuid;

            if (!ravenUuidForTopmostFeature) {
                return;
            }

            // If it's an asset, select it and don't do anything else
            if(topmostFeature.source === SOURCE_ASSETS_NAME){
                this.props.onSelectResource('asset', ravenUuidForTopmostFeature);

                return;
            }

            // check if the vehicle popup should be updated (this also avoids the popup disappearing when clicking the hovered/same vehicle marker)
            if (topmostFeature.properties.markerCategory) {

                if (topmostFeature.properties.markerCategory === "vehicle") {
                    // mapbox appears to automatically remove all popups on click
                    // unset this.mapboxVehiclePopup so that showVehiclePopupForRavenUuid can redundantly replace the fallaciously-dismissed vehicle popup
                    this.mapboxVehiclePopup = null; // align with mapbox's automatic popup dismissal behaviour
                    this.mapboxVehiclePopupIsStatic = false;

                    var raven = this.getRavenByUuid(this.mapSelectedRavenUuid);

                    if (this.mapSelectedRavenUuid !== ravenUuidForTopmostFeature) {
                        this.setHoveredFeatureStateForRavenUuid(false, this.mapSelectedRavenUuid );
                    } else {

                        this.registerVehiclePopupForRavenUuid(this.mapSelectedRavenUuid, raven, topmostFeature.geometry.coordinates, e.target);
                        return;
                    }
    
                    this.mapSelectedRavenUuid = ravenUuidForTopmostFeature;
                    this.setSelectedFeatureStateForRavenUuid(true, this.mapSelectedRavenUuid);
                    this.props.onSelectResource('vehicle', this.mapSelectedRavenUuid);

                    // replace the vehicle popup
                    this.registerVehiclePopupForRavenUuid(this.mapSelectedRavenUuid, raven, topmostFeature.geometry.coordinates, e.target);

                }
            }
        }
        else
        {
            // User didn't click on a Raven or asset, check if it is a geofence
            var featuresForClickedArea = this.map.queryRenderedFeatures(boundingBoxAroundClickedPoint, {layers: GEOFENCE_LAYER_IDS});

            if (featuresForClickedArea.length > 0) { // check if user is clicking away from a raven feature (i.e., the user is attempting to clear the selection)

                var topmostFeature = featuresForClickedArea[0];

                var geofenceId = topmostFeature.properties.id;

                this.props.onSelectResource('geofence', geofenceId);
            }
        }
    }

    onMapMoveEnd = (e) => {
        var originalEvent = e.originalEvent;
        if (!originalEvent) {
            return;
        }
        var eventClassName = originalEvent.constructor.name;
        if (!eventClassName) {
            return;
        }

        /* types of events that we can expect to be defined as user-related (snagged from https://raw.githubusercontent.com/DefinitelyTyped/DefinitelyTyped/master/types/mapbox-gl/index.d.ts)):
        movestart: MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>;
        move: MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>;
        moveend: MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>;

        zoomstart: MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>;
        zoom: MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>;
        zoomend: MapboxEvent<MouseEvent | TouchEvent | WheelEvent | undefined>;

        rotatestart: MapboxEvent<MouseEvent | TouchEvent | undefined>;
        rotate: MapboxEvent<MouseEvent | TouchEvent | undefined>;
        rotateend: MapboxEvent<MouseEvent | TouchEvent | undefined>;

        dragstart: MapboxEvent<MouseEvent | TouchEvent | undefined>;
        drag: MapboxEvent<MouseEvent | TouchEvent | undefined>;
        dragend: MapboxEvent<MouseEvent | TouchEvent | undefined>;

        pitchstart: MapboxEvent<MouseEvent | TouchEvent | undefined>;
        pitch: MapboxEvent<MouseEvent | TouchEvent | undefined>;
        pitchend: MapboxEvent<MouseEvent | TouchEvent | undefined>;
        */

        switch (eventClassName) {
            case "MouseEvent":
            case "TouchEvent":
            case "WheelEvent":
                break; // continue to process event as a user event
            default:
                return; // halt, this is an automated event
        }

        this.mapboxUserHasMovedMap = true;

    }

    onMapDragStart = (e) => {
        // https://docs.mapbox.com/mapbox-gl-js/api/#marker.event:dragstart
        //console.log("dragstart");
        if (this.mapboxControlIsFollowingSelectedRaven === true) {
            this.mapboxControlIsFollowingSelectedRaven = false;
            this.mapboxAddToggleFollowSelectedRavenControl();
        }
    }

    onMapMouseMoveGeofenceLayer = (e) => {
        // Change the cursor style as a UI indicator.
        this.map.getCanvas().style.cursor = 'pointer';

        var i;

        var newids = [];
        var newidsLookup = {};

        for(i = 0; i < e.features.length; i++)
        {
            var newid = e.features[i].id;

            newids.push(newid);

            newidsLookup[newid] = true;

            this.map.setFeatureState({source: SOURCE_GEOFENCE_LABEL_NAME, id: newid}, { visible: true});
        }

        if(this.currentHoverGeofenceIds)
        {
            for(i = 0; i < this.currentHoverGeofenceIds.length; i++)
            {
                var id = this.currentHoverGeofenceIds[i];
                if(newidsLookup[id])
                {
                    // old and new match, do nothing
                    continue;
                }

                // Old one doesn't exist any more, hide.
                this.map.setFeatureState({source: SOURCE_GEOFENCE_LABEL_NAME, id: id}, { visible: false});
        
            }
        }

        this.currentHoverGeofenceIds = newids;

    }
 
    onMapMouseLeaveGeofenceLayer = () => {
        if(!this.currentHoverGeofenceIds)
            return;

        var i;

        for(i = 0; i < this.currentHoverGeofenceIds.length; i++)
        {
            var id = this.currentHoverGeofenceIds[i];
            this.map.setFeatureState({source: SOURCE_GEOFENCE_LABEL_NAME, id: id}, { visible: false});
        }

        this.currentHoverGeofenceIds = [];

        this.map.getCanvas().style.cursor = '';
    }

    onSelectEvent = (e) => {

        /*{
        "media":[],
        "geoStamp":{
            "lat":45.4142006431104,
            "lon":-75.6978651756919,
            "ts":"2018-12-22T01:53:08"
        },
            "ts":"2018-12-22T01:53:08",
            "mediaAutomatch":null,
            "duration":0,
            "type":"EVENT_RAVEN_UNPLUGGED",
            "parked":false
        }*/
        if (!e.detail) {
            return;
        }

        const event = e.detail.event;

        if (!event) {
            return;
        }
        if (!event.geoStamp) {
            return;
        }
        if (!event.geoStamp.lon || !event.geoStamp.lat) {
            return;
        }

        if (this.mapboxControlIsFollowingSelectedRaven === true) {
            this.mapboxControlIsFollowingSelectedRaven = false;
            this.mapboxAddToggleFollowSelectedRavenControl();
        }

        var eventLon = event.geoStamp.lon;
        var eventLat = event.geoStamp.lat;

        if (OPTIMIZE_GPS_DATA === true) {
            eventLon = TelematicsHelper.optimizedDecimalPlaces(eventLon);
            eventLat = TelematicsHelper.optimizedDecimalPlaces(eventLat);
        }

        this.map.easeTo({
            center: [eventLon, eventLat], //https://docs.mapbox.com/mapbox-gl-js/api/#cameraoptions
            // use "ease in" and "ease out" to speed up towards the event location and then ease to a stop
            // TODO, these easing animations seem really subtle.  Are they actually working?  Check it the duration can be changed and then we can see for sure.
            easing: MapboxHelper.easingFunctions.easeInOutQuad, //https://docs.mapbox.com/mapbox-gl-js/api/#animationoptions + https://docs.mapbox.com/mapbox-gl-js/example/game-controls/ + https://gist.github.com/gre/1650294
        });
    }

    onLiveStreamAtTime = (ts) => {

        var selectedRavenUuid = this.getSelectedRavenUuidFromProps();

        var raven = this.getRavenByUuid(selectedRavenUuid);

        // user without permissions shouldn't reach here, 
        // just a double-check
        if(!raven.canViewVideoStream) {
            console.warn("User does not have access to video streams for raven " + selectedRavenUuid);
            return;
        }

        this.props.onLiveStream({
            ravenUuid: selectedRavenUuid,
            vehicleName: raven.vehicle.name,
            defaultCamera: 'CAMERA_ROAD',
            startDate: ts.getTime ? ts : ts.endsWith('Z') ? new Date(ts) : new Date("" + ts + "Z")

        });

    }

    mapboxToggleTrafficControlCallback = (mapboxControlShouldDisplayTraffic) => {
        this.mapboxControlShouldDisplayTrafficByDefault = mapboxControlShouldDisplayTraffic;
    }

    mapboxRemoveToggleTrafficControlControl = () => {
        this.map.removeControl(this.mapboxToggleTrafficControl);
        this.mapboxToggleTrafficControl = null;
    }

    mapboxAddToggleTrafficControl = () => {
        this.mapboxToggleTrafficControl = new MapboxToggleTrafficControl(
            this.mapboxControlShouldDisplayTrafficByDefault,
            this.mapboxToggleTrafficControlCallback
        );
        this.map.addControl(this.mapboxToggleTrafficControl);
    }

    mapboxToggleSatelliteControlCallback = (mapboxControlShouldDisplaySatellite) => {
        this.mapboxControlShouldDisplaySatelliteByDefault = mapboxControlShouldDisplaySatellite;
        this.mapLoaded = false;
        if (this.mapboxControlShouldDisplaySatelliteByDefault === true) {
            this.map.setStyle('mapbox://styles/dan-in-ottawa/' + MAPBOX_SATELLITE_STYLE_ID);
        } else {
            this.map.setStyle(this.props.theme !== 'light' ? 'mapbox://styles/dan-in-ottawa/' + MAPBOX_DEFAULT_DARK_STYLE_ID : 'mapbox://styles/dan-in-ottawa/' + MAPBOX_DEFAULT_LIGHT_STYLE_ID);
        }

        const scopedThis = this;
        clearTimeout(this.mapboxStyleChangedCompletedCheckInterval);
        this.mapboxStyleChangedCompletedCheckInterval = setInterval(() => {

            if (this.mapLoaded === true) {
                clearTimeout(this.mapboxStyleChangedCompletedCheckInterval);
                return;
            }

            const styleChangeCompleted = this.map.isStyleLoaded();
            if (styleChangeCompleted === true) {

                const imageNameArray = this.map.listImages();
                for (var i = 0; i < imageNameArray.length; i++) {
                    if (imageNameArray[i].substring(0, RUNTIME_MAP_FEATURES.VEHICLE_MARKER_PREFIX.length) === RUNTIME_MAP_FEATURES.VEHICLE_MARKER_PREFIX) { // TODO use a more clearly RESERVED prefix for dynamic image assets (avoid a colision with 'vehicleMarker-' if ever baked into the style in Mapbox Studio)
                        console.warn("Vehicle marker images appears to still be present from previous style.  Skipping attempt")
                        return;
                    }
                }

                this.mapLoaded = true;

                clearTimeout(this.mapboxStyleChangedCompletedCheckInterval);

                const darkThemeSelected = this.props.theme === 'dark';
                const useDarkThemeForStyle = mapboxControlShouldDisplaySatellite? true : darkThemeSelected; // Dark theme icons look the best for satellite (otherwise use the defaul)

                for (var ravenUuid in this.mapDataModel.ravenData) {

                    var raven = this.getRavenByUuid(ravenUuid);

                    if(!raven)
                        continue;

                    var driverName = this.getDriverName(raven);

                    // POINT data
                    var vehicleStatus = raven.isDriving ? "driving" : "parked";

                    var showProblemsBadge = raven.vehicle.activeDTCs.length > 0;

                    var coordinates = [
                        raven.lastLocation.lon,
                        raven.lastLocation.lat 
                    ];

                    var hasMultipleOccupants = raven.vehicle.hasPassengers; 

                    MapboxHelper.addVehicleMarkersAssetsForRaven(
                        this.map,
                        ravenUuid,
                        raven.vehicle.name,
                        driverName,
                        MapboxHelper.getVehicleMarkerState(false, vehicleStatus, showProblemsBadge, hasMultipleOccupants),
                        useDarkThemeForStyle
                    );
                }

                const assets = this.props.assets;
                if (assets && assets.length) {
                    assets.forEach(asset => {
                        const assetSelected = this.props.selectedResource.resourceType === "asset" && asset.assetUuid === this.props.selectedResource.resourceId;
                        
                        MapboxHelper.addAssetTrackerImagesToMap(this.map, 
                            asset.assetUuid, 
                            asset.name, 
                            MapboxHelper.getAssetMarketState(assetSelected),
                            useDarkThemeForStyle);
                    });
                }

                this.updateMapboxGeofences();

                // replace the data sources and layers added at runtime
                this.setMapSourceFromRavens();

                // trigger onStyleChange for other style-related custom controls
                if (this.mapboxToggleTrafficControl) {
                    this.mapboxToggleTrafficControl.onStyleChange();
                }
                if (this.mapboxToggleRavenTrailsControl) {
                    this.mapboxToggleRavenTrailsControl.onStyleChange();
                }
            }
        }, 100);
    }

    mapboxAddToggleSatelliteControl = () => {
        this.mapboxToggleSatelliteControl = new MapboxToggleSatelliteControl(
            this.mapboxControlShouldDisplaySatelliteByDefault,
            this.mapboxToggleSatelliteControlCallback
        );
        this.map.addControl(this.mapboxToggleSatelliteControl);
    }

    mapboxToggleRavenTrailsControlCallback = (visible) => {
        this.props.setMapTrailsVisible(visible);
        this.setAllMapTrailsVisible(visible);
    }

    setAllMapTrailsVisible = (visible) => {

        this.map.setLayoutProperty(LAYER_RAVEN_ROUTE_NAME, 'visibility', visible ? 'visible' : 'none');
        this.map.setLayoutProperty(LAYER_RAVEN_EVENTS_NAME, 'visibility', visible ? 'visible' : 'none');
        this.map.setLayoutProperty(LAYER_SELECTED_RAVEN_EVENTS_NAME, 'visibility', visible ? 'visible' : 'none');
        // this.map.setPaintProperty(LAYER_RAVEN_EVENTS_NAME, 'icon-opacity', visible ? 1 : 0);

    }

    mapboxAddToggleRavenTrailsControl = () => {
        this.mapboxToggleRavenTrailsControl = new MapboxToggleRavenTrailsControl(
            this.props.mapTrailsVisible,
            this.mapboxToggleRavenTrailsControlCallback
        );
        this.map.addControl(this.mapboxToggleRavenTrailsControl);
        this.setAllMapTrailsVisible(this.props.mapTrailsVisible);
    }

    mapboxToggleFollowSelectedRavenControlCallback = (mapboxControlShouldFollowSelectedRaven) => {

        this.mapboxControlIsFollowingSelectedRaven = mapboxControlShouldFollowSelectedRaven;

        if (this.mapboxControlIsFollowingSelectedRaven === true) {
            this.map.removeControl(this.mapboxToggleFollowSelectedRavenControl);
        }

        var selectedRavenUuid = this.getSelectedRavenUuidFromProps();
        if (selectedRavenUuid) {
            this.mapboxEaseToRaven(selectedRavenUuid);
        } else {
            // console.log("mapboxToggleFollowSelectedRavenControlCallback getSelectedRavenUuidFromProps returned null");
        }
    }

    mapboxRemoveToggleFollowSelectedRavenControl = () => {
        this.map.removeControl(this.mapboxToggleFollowSelectedRavenControl);
        this.mapboxToggleFollowSelectedRavenControl = null;
    }

    mapboxAddToggleFollowSelectedRavenControl = () => {
        this.mapboxToggleFollowSelectedRavenControl = new MapboxToggleFollowSelectedRavenControl(
            this.mapboxControlIsFollowingSelectedRaven,
            this.mapboxToggleFollowSelectedRavenControlCallback
        );
        this.map.addControl(this.mapboxToggleFollowSelectedRavenControl);
    }

    mapboxEaseToRaven = (ravenUuid) => {

        var selectedRavensLastPosition = this.getLastPosition(ravenUuid);

        if (!selectedRavensLastPosition) {
            return
        }

        this.map.easeTo({
            center: selectedRavensLastPosition, //https://docs.mapbox.com/mapbox-gl-js/api/#cameraoptions
            // use "ease out" (not with "ease in") to instantly start moving (to try catch up to the map update quickly), but ease out animation to make it less jittery:
            easing: MapboxHelper.easingFunctions.easeOutQuad, //https://docs.mapbox.com/mapbox-gl-js/api/#animationoptions + https://docs.mapbox.com/mapbox-gl-js/example/game-controls/ + https://gist.github.com/gre/1650294
        });
    }

    throttleFunction = (func, delay) => {

        if (this.sourceEnhancedVideoAccessRouteUpdateTimerId) {
            return;
        }
    
        this.sourceEnhancedVideoAccessRouteUpdateTimerId = setTimeout( () => {
            func()
            this.sourceEnhancedVideoAccessRouteUpdateTimerId = undefined;
        }, delay)
    }

    throttledEnhancedVideoAccessRouteHighlightRequestHandler = (event) => {
        this.throttleFunction(() => {
            this.enhancedVideoAccessRouteHighlight(event)
        }, ENHANCED_VIDEO_ACCESS_ROUTE_HIGHLIGHT_THROTTLE_DELAY);
    }
    
    enhancedVideoAccessRouteHighlight = (event) => {//(ravenUuid, startIndex = 0, endIndex = 0) => {

        if (!event.detail) {
            console.warn("mapboxMapDashboard enhancedVideoAccessRouteHighlight event.detail not found.");
            return;
        }

        const ravenUuid = event.detail.ravenUuid;
        const startIndex = event.detail.startIndex;
        const endIndex = event.detail.endIndex;

        if (!ravenUuid) {
            console.warn("mapboxMapDashboard enhancedVideoAccessRouteHighlight event.detail not found.");
            return;
        }

        if (!this.mapDataModel ||
            !this.mapDataModel.ravenData ||
            !this.mapDataModel.ravenData[ravenUuid] ||
            !this.mapDataModel.ravenData[ravenUuid].SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME) {
            console.error("mapboxMapDashboard enhancedVideoAccessRouteHighlight mapDataModel not ready", ravenUuid, this.mapDataModel);
            return;
        }

        const sourceEnhancedVideoAccessRoute = this.mapDataModel.ravenData[ravenUuid].SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME;
        if (!sourceEnhancedVideoAccessRoute ||
            !sourceEnhancedVideoAccessRoute.features ||
            !sourceEnhancedVideoAccessRoute.features[0].properties ||
            !sourceEnhancedVideoAccessRoute.features[0].geometry) {
            console.error("mapboxMapDashboard enhancedVideoAccessRouteHighlight sourceEnhancedVideoAccessRoute not found");
            return;
        }

        if (!this.sourceEnhancedVideoAccessRoute) { 
            this.sourceEnhancedVideoAccessRoute = this.map.getSource(SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME);
            if (!this.sourceEnhancedVideoAccessRoute) {
                console.warn("mapboxMapDashboard enhancedVideoAccessRouteHighlight SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME source not ready");
                return;
            }
        }
        
        this.sourceEnhancedVideoAccessRoute.setData({
            type: sourceEnhancedVideoAccessRoute.type,
            features: [{
                properties: {
                    ravenUuid: sourceEnhancedVideoAccessRoute.features[0].properties.ravenUuid,
                    status: sourceEnhancedVideoAccessRoute.features[0].properties.status,
                    lineCategory: sourceEnhancedVideoAccessRoute.features[0].properties.lineCategory,
                    timestamps: sourceEnhancedVideoAccessRoute.features[0].properties.timestamps.slice(startIndex, endIndex)
                },
                geometry: {
                    type: sourceEnhancedVideoAccessRoute.features[0].geometry.type,
                    coordinates: sourceEnhancedVideoAccessRoute.features[0].geometry.coordinates.slice(startIndex, endIndex)
                },
                id: sourceEnhancedVideoAccessRoute.id
            }]
        });
    }

    addEmptySourcesAndLayers = (map) => {

        var mapboxMap = map;

        var blankFeatureCollection = {
            'type': 'FeatureCollection',
            'features': []
        };

        // initiate blanksources and layers

        
        // Assets
        mapboxMap.addSource(SOURCE_ASSETS_NAME,
            {
                type: 'geojson',
                data: blankFeatureCollection,
            }
        );

        mapboxMap.addLayer(MapboxHelper.clusterMarkerSymbolLayer(
            SOURCE_ASSETS_NAME,
            LAYER_CLUSTERS_ASSETS_NAME
        ));

        mapboxMap.addLayer(MapboxHelper.deselectedMarkerSymbolLayerForRavensPointsTypes(
            SOURCE_ASSETS_NAME,
            LAYER_ASSETS_NAME
        ), LAYER_CLUSTERS_ASSETS_NAME);


        // Vehicle Points
            // initiate source and layer
            var sourceFeatureCollectionForAllRavensVehiclesPoints = {
                type: 'geojson',
                data: blankFeatureCollection
            }
            mapboxMap.addSource(SOURCE_RAVEN_VEHICLE_POINTS_NAME, sourceFeatureCollectionForAllRavensVehiclesPoints);
            mapboxMap.addSource(SOURCE_SELECTED_RAVEN_VEHICLE_POINTS_NAME, sourceFeatureCollectionForAllRavensVehiclesPoints);

            mapboxMap.addLayer(MapboxHelper.selectedMarkerSymbolLayerForRavensPointsTypes(
                 SOURCE_SELECTED_RAVEN_VEHICLE_POINTS_NAME,
                 LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME
            ));

            mapboxMap.addLayer(MapboxHelper.deselectedMarkerSymbolLayerForRavensPointsTypes(
                 SOURCE_RAVEN_VEHICLE_POINTS_NAME,
                 LAYER_RAVEN_VEHICLE_POINTS_NAME
            ), LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME); // place it under "klashwerks_all_ravens_vehicles_points_selected_layer"


        // EVENTS
            // initiate source and layer
            var sourceFeatureCollectionForAllRavensEventsPoints = {
                type: 'geojson',
                data: blankFeatureCollection
            }/*
            if (OPTIMIZE_GEOMETRIES_VIA_TOLERANCE === true) {
                sourceFeatureCollectionForAllRavensEventsPoints.tolerance = 3.5;
            }*/

            mapboxMap.addSource(SOURCE_RAVEN_EVENTS_NAME, sourceFeatureCollectionForAllRavensEventsPoints);

            mapboxMap.addLayer(MapboxHelper.selectedMarkerSymbolLayerForRavensPointsTypes(
                SOURCE_RAVEN_EVENTS_NAME,
                LAYER_SELECTED_RAVEN_EVENTS_NAME 
            ), LAYER_RAVEN_VEHICLE_POINTS_NAME); // place it under "klashwerks_all_ravens_vehicles_points_layer"

            mapboxMap.addLayer(MapboxHelper.deselectedMarkerSymbolLayerForRavensPointsTypes(
                SOURCE_RAVEN_EVENTS_NAME,
                LAYER_RAVEN_EVENTS_NAME 
            ), LAYER_SELECTED_RAVEN_EVENTS_NAME ); // place it under "klashwerks_all_ravens_events_points_selected_layer"

        // ENHANCED VIDEO ACCESS HIGHLIGHT ROUTE
          // initiate source and layer
          var sourceFeatureCollectionForEnhancedVideoAccessRouteHighlightLine = {
                type: 'geojson',
                data: blankFeatureCollection
          }

          if (OPTIMIZE_GEOMETRIES_VIA_TOLERANCE === true) {
            sourceFeatureCollectionForEnhancedVideoAccessRouteHighlightLine.tolerance = 3.5;
          }

          mapboxMap.addSource(SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME, sourceFeatureCollectionForEnhancedVideoAccessRouteHighlightLine);

          mapboxMap.addLayer({
            "id": LAYER_ENHANCED_VIDEO_ACCESS_ROUTE_NAME,
            "type": "line",
            "source": SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME,
            "paint": {
                "line-color": "#B2D8FF",
                "line-opacity": ["case",

                        ["boolean", ["feature-state", "trailsHidden"], false],
                        // hidden
                        0,

                        ["boolean", ["feature-state", "isHidden"], false],
                        // hidden
                        0,

                        // fallback is not hidden
                        ["boolean", ["feature-state", "hoverOrSelected"], false],
                        0.8,

                        // Default value
                        0.8
                    ],
                "line-width": 24,
            },
            "layout": {
              "visibility": "visible",
              "line-join": "round", //"bevel", "round", "miter" (default)
              "line-cap": "round" //"butt" (default), "round", "square"
            }
          }, LAYER_RAVEN_EVENTS_NAME); // add it underneath Mapbox Studio layer "layer-0_pin-symbol-background" for "Raven Web - Light" theme.

        // ROUTE
          // initiate source and layer
          var sourceFeatureCollectionForAllRavensRouteLines = {
                type: 'geojson',
                data: blankFeatureCollection
          }

          if (OPTIMIZE_GEOMETRIES_VIA_TOLERANCE === true) {
            sourceFeatureCollectionForAllRavensRouteLines.tolerance = 3.5;
          }

          mapboxMap.addSource(SOURCE_RAVEN_ROUTE_NAME, sourceFeatureCollectionForAllRavensRouteLines);

          mapboxMap.addLayer({
            "id": LAYER_RAVEN_ROUTE_NAME,
            "type": "line",
            "source": SOURCE_RAVEN_ROUTE_NAME,
            "paint": {
                "line-color": "#0079D1",/*[ 'case', // If the feature-state called 'notselected' is true, then the opacity should be blue ("#009DCB"), otherwise it should be "#FF0000" (defaults to blue if feature-state does not exist)
                        ['boolean', ['feature-state', 'hoverOrSelected'], false],
                        "#009DCB",
                        "#FF0000"
                    ],*/
                "line-opacity": ["case",

                        ["boolean", ["feature-state", "trailsHidden"], false],
                        // hidden
                        0,

                        ["boolean", ["feature-state", "isHidden"], false],
                        // hidden
                        0,

                        // fallback is not hidden
                        ["boolean", ["feature-state", "hoverOrSelected"], false],
                        1,

                        // Default value
                        0.5
                    ],
                "line-width": 8,
            },
            "layout": {
              "visibility": "visible",
              "line-join": "round", //"bevel", "round", "miter" (default)
              "line-cap": "round" //"butt" (default), "round", "square"
            }
          }, LAYER_ENHANCED_VIDEO_ACCESS_ROUTE_NAME); // add it underneath Enhanced Video Access route highlight

        // GEOFENCES

            var sourceFeatureCollectionForAllGeofences = {
                type: 'geojson',
                data: blankFeatureCollection
            };

            var labelSource = {
                type: 'geojson',
                data: blankFeatureCollection
            };

            mapboxMap.addSource(SOURCE_GEOFENCE_NAME, sourceFeatureCollectionForAllGeofences);
            mapboxMap.addSource(SOURCE_GEOFENCE_LABEL_NAME, labelSource);

            var geofence_layer = {
                "id": LAYER_IDS.GEOFENCES.POLYGONS,
                "type": "fill",
                "source": SOURCE_GEOFENCE_NAME,
                "paint": {
                    "fill-opacity": ["match",
                        ["get", "selected"],
                        ["1"],
                        0.5,
                        0.25,
                    ],
                    "fill-color": ["case",
                        ["boolean", ["feature-state", "isHidden"], false],
                        // hidden
                        ['literal', '#555555'],
                        // fallback
                        ['coalesce', ['get', 'fill-color'], '#555555'],
                    ],
                    "fill-outline-color": ["case",
                        ["boolean", ["feature-state", "isHidden"], false],
                        // hidden
                        // ['rgba', 0,0,0,0],
                        ['literal', '#555555'],
                        // fallback
                        ['coalesce', ['get', 'fill-color'], '#555555'],
                    ],
                }
            };

            var geofence_outline_layer = {
                "id": LAYER_IDS.GEOFENCES.SELECTED_OUTLINE,
                "type": "line",
                "source": SOURCE_GEOFENCE_NAME,
                "paint": {
                    "line-width": [
                        "interpolate", ["exponential", 0.5], ["zoom"],
                        // zoom is 11 (or less) -> line width 0
                        11, 0,
                        // zoom is 20 (or greater) -> line width 7
                        20, 7
                    ],
                    "line-color": ['coalesce', ['get', 'outline-color'], '#555555'],
                    "line-opacity": ["match",
                        ["get", "selected"],
                        ["1"],
                        1,
                        0,
                    ],
                },
            };

            var geofence_label_layer = {
                "id": LAYER_IDS.GEOFENCES.LABELS,
                "type": "symbol",
                "source": SOURCE_GEOFENCE_LABEL_NAME,
                "paint": {
                    "text-opacity": ["step",
                        ["zoom"],
                            ["case",
                                ["boolean", ["feature-state", "visible"], false],
                                1,
                                0,
                            ],
                        14, // zoom level 10=large roads, 15=buildings
                        1,
                    ],
                    // "text-opacity": 1.0,

                    "text-color": ["get", "text-color"],
                },
                "layout": {
                    "text-field": ["get", "place_name"],
                    "text-justify": "center",
                    "text-allow-overlap": true,
                    "icon-allow-overlap": true,
                }
            };

            mapboxMap.addLayer(geofence_layer, LAYER_ASSETS_NAME);
            mapboxMap.addLayer(geofence_outline_layer, LAYER_ASSETS_NAME);
            mapboxMap.addLayer(geofence_label_layer);
    }

    updateMapboxGeofences = () => {

        //  FIXME - determine if geofences need changing

        if(!this.mapLoaded) return;

        var features = [];
        var labels = [];

        var selectedGeofenceId = null;

        const darkThemeSelected = this.props.theme === 'dark';

        const { resourceType, resourceId } = this.props.selectedResource;

        if(resourceType === "geofence")
        {
            selectedGeofenceId  = resourceId;
        }

        this.props.activeGeofenceIds.forEach( (id) => {

            const isVisible = this.props.isGeofenceVisible(id);
            if(!isVisible) return;

            const name = this.props.geofenceName(id);
            const geojson = this.props.geofenceGeojson(id);
            const color = this.props.geofenceColor(id);

            if (!geojson || !geojson.geometry || !geojson.properties) return;

            if(geojson.geometry.type === "Point"){
                features.push( MapboxHelper.createGeoJSONCircle(
                    geojson.geometry.coordinates,
                    geojson.properties.radius/1000,
                    {
                        id,
                        "place_name": name,
                        "fill-color": color,
                        "outline-color": color,
                        "selected": (id === selectedGeofenceId) ? "1" : "0"
                    })
                );
            }

            if(geojson.geometry.type === "Polygon" && (!this.props.geofenceGeometryEditModeActive || id !== selectedGeofenceId)){
                features.push( MapboxHelper.createGeoJSONPolygon(
                    geojson.geometry.coordinates,
                    {
                        id,
                        "place_name": name,
                        "fill-color": color,
                        "outline-color": color,
                        "selected": (id === selectedGeofenceId) ? "1" : "0"
                    })
                );
            }

            const labelCoordinates = geojson.geometry.type === "Polygon" ? MapboxHelper.getCenterOfPolygon(geojson.geometry.coordinates) : geojson.geometry.coordinates;

            labels.push( {
                "type": "Feature",
                "properties": {
                    id,
                    place_name: name,
                    "text-color": (darkThemeSelected || ColorUtil.isReallyDark(color)) ? "#ffffff" : "#000",
                },
                "geometry": {
                    "type": "Point",
                    "coordinates": labelCoordinates
                },
                id: id,
            });


        });

        var updatedFeatureCollection = {
            'type': 'FeatureCollection',
            'features': features
        };

        var updatedLabelCollection = {
            'type': 'FeatureCollection',
            'features': labels
        };
        const sourceGeofenceName = this.map.getSource(SOURCE_GEOFENCE_NAME);
        const sourceGeofenceLabelName = this.map.getSource(SOURCE_GEOFENCE_LABEL_NAME);
        if (!sourceGeofenceName || !sourceGeofenceLabelName) {
            this.addEmptySourcesAndLayers(this.map);
        }
        this.map.getSource(SOURCE_GEOFENCE_NAME).setData(updatedFeatureCollection);
        this.map.getSource(SOURCE_GEOFENCE_LABEL_NAME).setData(updatedLabelCollection);

        MapboxHelper.setGeofenceLayersVisible(this.map, this.geofenceLayersVisible);

    }


    needNewMarkerAssetForRaven = (ravenUuid, index, vehicleName, driverName, vehicleState, darkTheme) =>
    {
        if(!this.mapDataModel.ravenData[ravenUuid])
        {
            this.mapDataModel.ravenData[ravenUuid] = {
                vehicleName: vehicleName,
                driverName: driverName,
                vehicleState: vehicleState,
                darkTheme: darkTheme,
                id: index,
            };
            return true;
        }

        var entry = this.mapDataModel.ravenData[ravenUuid];

        if(entry.vehicleName === vehicleName && 
            entry.driverName === driverName && 
            entry.vehicleState === vehicleState && 
            entry.darkTheme === darkTheme)
        {
            return false;
        }

        entry.vehicleName = vehicleName;
        entry.driverName = driverName;
        entry.vehicleState = vehicleState;
        entry.darkTheme = darkTheme;

        this.mapDataModel.ravenData[ravenUuid] = entry;

        return true;

    }

    getRavenByUuid = (uuid) => {
        /*
        if (this.mapDataModel.ravenData.hasOwnProperty(uuid)) {
            if(this.mapDataModel.ravenData[uuid].index)
            {
                return this.props.ravens[this.mapDataModel.ravenData[uuid].index];
            }
        }
        */

        for(var i = 0; i < this.props.ravens.length; i++)
        {
            if(this.props.ravens[i].ravenUuid === uuid)
            {
                return this.props.ravens[i];
            }
        }
        return null;
    }

    getSelectedRavenUuidFromProps = optProps => {
        const props = optProps || this.props;
        const { resourceType, resourceId } = props.selectedResource || {};
        return resourceType === 'vehicle' && resourceId;
    }

    getSelectedassetUuid = optProps => {
        const props = optProps || this.props;
        const { resourceType, resourceId } = props.selectedResource || {};
        return resourceType === 'asset' && resourceId;
    }

    setSelectedFeatureStateForRavenUuid = (hoveredStateBool, ravenUuid) => {

        // All feature collections are the same size, the id is the index into the array
        var ravenData = this.mapDataModel.ravenData[ravenUuid];
        if(!ravenData)
        {
            return;
        }

        var targetRaven = this.getRavenByUuid(ravenUuid);

        // Update picture of currently selected Raven, and newly selected Raven

        var vehicleStatus = targetRaven.isDriving ? "driving" : "parked";
        var vehicleName = targetRaven.vehicle.name;
        var driverName = this.getDriverName(targetRaven);
        var showProblemsBadge = targetRaven.vehicle.activeDTCs.length > 0;
        var hasMultipleOccupants = targetRaven.vehicle.hasPassengers; 

        const darkThemeSelected = this.props.theme === 'dark';

        var vehicleMarkerState = MapboxHelper.getVehicleMarkerState(false, vehicleStatus, showProblemsBadge, hasMultipleOccupants);

        if(this.needNewMarkerAssetForRaven( ravenUuid, ravenData.id, vehicleName, driverName, vehicleMarkerState, darkThemeSelected))
        {
            MapboxHelper.addVehicleMarkersAssetsForRaven (this.map, ravenUuid, vehicleName, driverName, vehicleMarkerState, darkThemeSelected, true);
        }

        var uniqId = this.mapDataModel.ravenData[ravenUuid].id;

        this.map.setFeatureState({source: SOURCE_RAVEN_ROUTE_NAME, id: uniqId}, { hoverOrSelected: hoveredStateBool});
        this.map.setFeatureState({source: SOURCE_RAVEN_VEHICLE_POINTS_NAME, id: uniqId}, { hoverOrSelected: hoveredStateBool});
        this.map.setFeatureState({source: SOURCE_RAVEN_EVENTS_NAME, id: uniqId}, { hoverOrSelected: hoveredStateBool});

    }

    registerVehiclePopupForRavenUuid = (ravenUuid, raven, coordinates, addToTarget) => {
        if(this.state.vehiclePopup && this.state.vehiclePopup.ravenUuid !== ravenUuid)
        {
            this.hideVehiclePopupForRavenUuid ();
        }

        this.setState({
            vehiclePopup: { 
                ravenUuid: ravenUuid,
                raven: {...raven},
                coordinates: coordinates,
                addToTarget: addToTarget
            }
        });
    }

    unregisterVehiclePopupForRavenUuid = () => {
        this.setState({
            vehiclePopup: null
        });
    }

    createVehiclePopupContent = (targetRaven) =>
    {

        var mouseEnterPopup = () => {
            if(this.mapboxVehiclePopupTimeout)
            {
                clearTimeout(this.mapboxVehiclePopupTimeout);
                this.mapboxVehiclePopupTimeout = null;
            };
        };

        var onMessageBoxBlur = () => {
            this.mapboxVehiclePopupIsStatic = false;
        }

        var onMessageBoxFocus = () => {
            this.mapboxVehiclePopupIsStatic = true;
        }

        var onDTCsBlur = () => {
            this.mapboxVehiclePopupIsStatic = false;
        }

        var onDTCsFocus = () => {
            this.mapboxVehiclePopupIsStatic = true;
        }

        var onCameraSwitch = (index) => {
            this.mapboxVehiclePopupCameraIndex = index;
        }

        var popupContent = document.createElement('div');
        ReactDOM.render(<VehiclePopup
                features={this.props.features}
                showcaseTargetRavenUuid={this.props.showcaseTargetRavenUuid}
                stage={this.props.user.stage}
                raven={targetRaven}
                onMouseEnter={mouseEnterPopup}
                onMessageBoxBlur={onMessageBoxBlur}
                onMessageBoxFocus={onMessageBoxFocus}
                onDTCsBlur={onMessageBoxBlur}
                onDTCsFocus={onMessageBoxFocus}
                unitOfMeasure={this.props.unitOfMeasure}
                cameraIndex={this.mapboxVehiclePopupCameraIndex}
                onCameraSwitch={onCameraSwitch}
                canDispatchMessage={this.props.canSendDriverMessage(targetRaven.ravenUuid)}
            /> , popupContent);

        return popupContent;
    }

    showVehiclePopupForRavenUuid = (ravenUuid, coordinates, addToTarget) => {

        if (this.mapboxVehiclePopup) {
            // If the message is being edited, don't get rid of the popup
            if (this.mapboxVehiclePopupIsStatic === true) {
                return;
            }
            if (this.mapboxVehiclePopup.ravenUuid === ravenUuid) {

                // We've already got the popup, see if we need to change it

                var targetRaven = this.getRavenByUuid(ravenUuid);

                if (this.mapboxVehiclePopup.ravenUuid === ravenUuid) {

                    if (!targetRaven) {
                        return;
                    }

                    let popupContent;

                    // See if we need to refresh.  If anything displayed in the popup has changed:

                    // has currentFuel changed?
                    if (this.mapboxVehiclePopup.raven.vehicleState.currentFuel !== targetRaven.vehicleState.currentFuel) {

                        popupContent = this.createVehiclePopupContent(targetRaven);

                    // has currentSpeed changed?
                    } else if (this.mapboxVehiclePopup.raven.vehicleState.currentSpeed !== targetRaven.vehicleState.currentSpeed) {

                        popupContent = this.createVehiclePopupContent(targetRaven);

                    // check preview timestamps and image data
                    } else {

                        const vehiclePopupCurrentPreviewUrls = this.mapboxVehiclePopup.raven.vehicleState.currentPreviewUrls;
                        const targetRavenCurrentPreviewUrls = targetRaven.vehicleState.currentPreviewUrls;
                        const vehiclePopupCurrentPreviewUrlsTs = vehiclePopupCurrentPreviewUrls && vehiclePopupCurrentPreviewUrls.length > 0 && vehiclePopupCurrentPreviewUrls[0] ? vehiclePopupCurrentPreviewUrls[0].ts : null;
                        const targetRavenCurrentPreviewUrlsTs = targetRavenCurrentPreviewUrls && targetRavenCurrentPreviewUrls.length > 0 && targetRavenCurrentPreviewUrls[0] ? targetRavenCurrentPreviewUrls[0].ts : null;

                        // new preview URL?
                        if (vehiclePopupCurrentPreviewUrlsTs !== targetRavenCurrentPreviewUrlsTs) {

                            popupContent = this.createVehiclePopupContent(targetRaven);

                        // has image data or new image data loaded ?
                        } else {

                            const vehiclePopupLiveMedia = this.mapboxVehiclePopup.raven.liveMedia;
                            const targetRavenLiveMedia = targetRaven.liveMedia;
                            const vehiclePopupLiveMediaImageData = vehiclePopupLiveMedia && vehiclePopupLiveMedia.length > 0 && vehiclePopupLiveMedia[0] && vehiclePopupLiveMedia[0].imageData ? vehiclePopupLiveMedia[0].imageData.length : 0;
                            const targetRavenLiveMediaImageData = targetRavenLiveMedia && targetRavenLiveMedia.length > 0 && targetRavenLiveMedia[0] && targetRavenLiveMedia[0].imageData ? targetRavenLiveMedia[0].imageData.length : 0;

                            if (vehiclePopupLiveMediaImageData !== targetRavenLiveMediaImageData) {
                                popupContent = this.createVehiclePopupContent(targetRaven);
                            }
                        }
                    }
                    
                    if (!popupContent) {
                        return;
                    }

                    this.mapboxVehiclePopup.DOMelement.setDOMContent(popupContent);
                }
                return;

            } else {
                this.unregisterVehiclePopupForRavenUuid(); // tell current mapboxVehiclePopup's ravenUuid to "dismiss self"
            }
        }
        this.mapboxVehiclePopup = {}; // reset
        this.mapboxVehiclePopupIsStatic = false;

        // check for clone raven uuid and sanitize for props lookup
        var targetRavenUuid = ravenUuid;
        if (ravenUuid.includes("_clone")) {
            var cloneRavenUuidComponents = ravenUuid.split("_");
            targetRavenUuid = cloneRavenUuidComponents[0];
        }

        // check if target raven is hidden (the raven's features may be still present in the data source for usually up to 1 second, this check is only to handle that 1 second period before the raven is removed from the map)

        if (this.mapDataModel.ravenData.hasOwnProperty(ravenUuid)) {
            if (this.mapDataModel.ravenData[ravenUuid].isHidden === true) {
                return;
            }
        } else {
        }

        try {
            var targetRaven = this.getRavenByUuid(ravenUuid);

            var popupContent = this.createVehiclePopupContent(targetRaven);
            if(!popupContent)
            {
                return;
            }

            var newPopup = new mapboxgl.Popup({ offset: 26.5 })
                .setLngLat(coordinates)
                .setDOMContent(popupContent)
                .addTo(addToTarget);
            const popupElement = newPopup.getElement(); // https://docs.mapbox.com/mapbox-gl-js/api/markers/#popup#getelement
            popupElement.style.minWidth = "20rem"; // increase the minimum width for this specific popup (without affecting mapbox's default styling for smaller popups), otherwise this popup will not be wide enough to contain the vehicle details element
            newPopup.ravenUuid = ravenUuid;
            // TODO investigate using options.className, Mapbox API docs
            popupContent.parentNode.parentNode.className += " vehicleMarkerVerticalOffset";

            this.mapboxVehiclePopup.DOMelement = newPopup;
            this.mapboxVehiclePopup.ravenUuid = ravenUuid;
            this.mapboxVehiclePopup.raven = {...targetRaven};

        } catch (error) {
        }
    }

    hideVehiclePopupForRavenUuid = () => {

        if(this.mapboxVehiclePopupTimeout)
            clearTimeout(this.mapboxVehiclePopupTimeout);


        if (this.mapboxVehiclePopup.DOMelement) {

            this.mapboxVehiclePopup.DOMelement.remove();
            this.mapboxVehiclePopup = null; // unset

        }
    }

    getDriverName = (raven) => {
        var facialRecognitionEnabled = this.props.features && this.props.features.facial_recognition && raven.isDriving;

        var driverName;

        if (facialRecognitionEnabled) {
            if (raven.vehicle.currentDriver) { // the property, "vehicle", is populated via lambdaUtil getRavens query
                if (raven.vehicle.currentDriver.name) {
                    driverName = raven.vehicle.currentDriver.name;
                } else {
                    driverName = strings.unknown_driver;
                }
            } else {
                driverName = strings.no_driver_detected;
            }
        }

        return driverName;
    }

    //#region  creating features
    setMapSourceFromRavens = () => {
        const { ravens } = this.props;

        const darkThemeSelected = this.props.theme === 'dark';

        if (!ravens || !ravens.length) return;

        // POINT FEATURE COLLECTION
        var pointFeatureCollection = {
            'type': 'FeatureCollection',
            'features': []
        };
        var selectedFeatureCollection = {
            'type': 'FeatureCollection',
            'features': []
        };
        var routeFeatureCollection = {
            'type': 'FeatureCollection',
            'features': []
        };

        var eventFeatureCollection = {
            'type': 'FeatureCollection',
            'features': []
        };

        var assetsFeatureCollection = {
            'type': 'FeatureCollection',
            'features': []
        }

        var boundsForAllRavens = new mapboxgl.LngLatBounds();

        const selectedRavenUuid = this.getSelectedRavenUuidFromProps();

        // RAVENS (IF ANY)
        for (var i = 0; i < ravens.length; i++) {

            // If there is no ravenUuid or no last location, don't render it
            if (!ravens[i].hasOwnProperty("ravenUuid") || !ravens[i].lastLocation) {
                pointFeatureCollection.features.push(
                    MapboxHelper.pointTypeFeatureForRavensCurrentPosition (
                        null,
                        i,
                        null,
                        "unknown",
                        false,
                        false
                    )
                );

                continue;
            }

            var hidden = ravens[i].hidden;

            if (!this.mapDataModel.ravenData.hasOwnProperty(ravenUuid)) {
                this.mapDataModel.ravenData[ravenUuid] = {
                    isHidden: hidden,
                    id: i,
                    vehicleName: null,
                    driverName: null,
                    vehicleState: null,
                    darkTheme: null,
                }
            }

            var uniqId = this.mapDataModel.ravenData[ravens[i].ravenUuid];

            var pointFeature = this.generatePointFeature(uniqId, ravens[i]);

            boundsForAllRavens.extend(pointFeature.geometry.coordinates);

            // Add the image to the map
            var driverName = this.getDriverName(ravens[i]);
            var ravenUuid = ravens[i].ravenUuid;
            var vehicleStatus = ravens[i].isDriving ? "driving" : "parked";
            var showProblemsBadge = ravens[i].vehicle.activeDTCs.length > 0;
            var hasMultipleOccupants = ravens[i].vehicle.hasPassengers; 

            const darkThemeSelected = this.props.theme === 'dark';

            if(this.needNewMarkerAssetForRaven( 
                    ravenUuid,
                    i,
                    ravens[i].vehicle.name,
                    driverName,
                    MapboxHelper.getVehicleMarkerState(selectedRavenUuid === ravenUuid, vehicleStatus, showProblemsBadge, hasMultipleOccupants),
                    darkThemeSelected 
            ))
            {
                MapboxHelper.addVehicleMarkersAssetsForRaven(
                    this.map,
                    ravenUuid,
                    ravens[i].vehicle.name,
                    driverName,
                    MapboxHelper.getVehicleMarkerState(selectedRavenUuid === ravenUuid, vehicleStatus, showProblemsBadge, hasMultipleOccupants),
                    darkThemeSelected 
                );
            }

            if(selectedRavenUuid === ravenUuid)
            {
                // Don't show hidden Raven, even if it is selected
                if(hidden) continue;
                // selected Raven to source on top

                // Add route to view (copy logic from updateMapboxRouteLineFeatureCollectionForAllRavens) 
                routeFeatureCollection = this.updateMapboxRouteLineFeatureCollectionForRaven(ravens[i]);

                // TODO - fine tune this method for tracking a off-map optimized source for map content
                this.mapDataModel.ravenData[ravens[i].ravenUuid].SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME = routeFeatureCollection;

                eventFeatureCollection = this.updateMapboxPointFeatureCollectionForRavenEvents(ravens[i]);

                selectedFeatureCollection.features.push(pointFeature);

                pointFeatureCollection.features.push(
                    MapboxHelper.pointTypeFeatureForRavensCurrentPosition (
                        null,
                        i,
                        null,
                        "unknown",
                        false,
                        false
                    )
                );

            }
            else
            {
                if(hidden)
                {
                    pointFeatureCollection.features.push(
                        MapboxHelper.pointTypeFeatureForRavensCurrentPosition (
                            null,
                            i,
                            null,
                            "unknown",
                            false,
                            false
                        )
                );
                }
                else
                {
                    pointFeatureCollection.features.push(pointFeature);
                }
            }
        }

        // ASSETS (IF ANY)
        const {assets} = this.props;
        const [assetFeatures, assetSelectedFeature] = this.generateAssetsFeatures(boundsForAllRavens, true);
        
        assetsFeatureCollection.features = assetFeatures;
        if(assetSelectedFeature) {
            selectedFeatureCollection.features.push(assetSelectedFeature);
        }

        this.mapboxDataLastUpdate = Date.now();
        this.forceUpdateOfMapboxData(
            pointFeatureCollection,
            selectedFeatureCollection,
            eventFeatureCollection,
            routeFeatureCollection,
            assetsFeatureCollection
        );

        this.updateMapboxGeofences();



        if(this.mapLoaded) {
            const selectedRavenUuid = this.getSelectedRavenUuidFromProps();
            if (this.mapSelectedRavenUuid !== selectedRavenUuid) {
                // remove previously select raven's enhanced route highlight source from mapDataModel, if found
                if (this.mapSelectedRavenUuid &&
                    this.mapDataModel &&
                    this.mapDataModel.ravenData &&
                    this.mapDataModel.ravenData[this.mapSelectedRavenUuid]) {

                    delete this.mapDataModel.ravenData[this.mapSelectedRavenUuid].SOURCE_ENHANCED_VIDEO_ACCESS_ROUTE_NAME;
                }
                // ensure rendered enahnced route highlight is cleared, if found
                if (this.sourceEnhancedVideoAccessRoute) {
                    this.sourceEnhancedVideoAccessRoute.setData({ // clear selection
                        'type': 'FeatureCollection',
                        'features': []
                    });
                }

                if (this.mapSelectedRavenUuid) {
                    this.unregisterVehiclePopupForRavenUuid(); // RAV-562 - Close Vehicle Popup when changing Ravens
                }

                this.mapSelectedRavenUuid = selectedRavenUuid;
                ravens.forEach(raven => {
                    this.setSelectedFeatureStateForRavenUuid(raven.ravenUuid === selectedRavenUuid, raven.ravenUuid);
                });
            }
            
            if (assets) {
                const selectedassetUuid = this.getSelectedassetUuid();
                assets.forEach(asset => {
                    this.setSelectedFeatureStateForAsset(asset.assetUuid === selectedassetUuid, asset.assetUuid)
                });
            }
        }
    }


    calculateClusterRadius(zoom) {
        const [minRadius, maxRadius] = ZOOM_RADIUS_MAPPING[Math.floor(zoom)];

        if(minRadius === maxRadius) return minRadius;
    
        // For each zoom level, we have a min and max radius. We need to calculate the radius based on the zoom level
        // For each zoom level we support 4 different radius values based on the fraction part of the zoom level
        // This is to make the cluster radius change smoothly as we zoom in/out (but not too many times)
        const fraction = Math.round((zoom % 1) * 100) / 100;
        if (fraction <= 0.25) {
            return maxRadius;
        } else if (fraction <= 0.5) {
            return minRadius + (maxRadius - minRadius) * 0.75; 
        } else if (fraction <= 0.75) {
            return minRadius + (maxRadius - minRadius) * 0.5;
        } else {
            return minRadius;
        }
    }

    generateAssetsFeatures = (bounds = undefined, cluster = true) => {
        const { assets } = this.props;

        const darkThemeSelected = this.props.theme === 'dark';

        const features = [];
        let selectedFeature;

        if (assets && assets.length) {
            if (!this.assetTrackerImages){
                this.assetTrackerImages = {};
            }

            assets.forEach(asset => {
                if(asset.latitude && asset.longitude && asset.visible) {
                    const assetSelected = (this.props.selectedResource.resourceType === "asset" && asset.assetUuid === this.props.selectedResource.resourceId);

                    const assetState = MapboxHelper.getAssetMarketState(assetSelected);

                    // RAV-5451: Besides changing the Pin when asset's selection state changes,
                    // we need to update the asset Pin if the name of asset changes
                    const assetTrackerImageKey = asset.name + "-" + assetState;

                    if (!this.assetTrackerImages[asset.assetUuid] || this.assetTrackerImages[asset.assetUuid] !== assetTrackerImageKey) {
                        MapboxHelper.addAssetTrackerImagesToMap(this.map, asset.assetUuid, asset.name, assetState, darkThemeSelected);
                        this.assetTrackerImages[asset.assetUuid] = assetTrackerImageKey;
                    }

                    asset.featureId = asset.assetUuid;
                
                    const assetFeature = MapboxHelper.pointTypeFeatureForRavensCurrentPosition(
                        [asset.longitude, asset.latitude],
                        asset.featureId,
                        asset.assetUuid,
                        'parked'
                    );

                    assetFeature.properties.markerCategory = "asset"; // RAV-5478: Add markerCategory property to distinguish between assets and ravens
                    assetFeature.properties.assetName = asset.name; // RAV-5475: To show asset's name in the cluster. I think this is better than looking up the asset name from the props.assets array in a loop when rendering the cluster
                    
                    if(bounds){
                        bounds.extend(assetFeature.geometry.coordinates);
                    }

                    if(assetSelected) {
                        selectedFeature = assetFeature;

                        const placeHolderAssetPoint =MapboxHelper.pointTypeFeatureForRavensCurrentPosition(
                            [asset.longitude, asset.latitude],
                            asset.featureId,
                            asset.assetUuid,
                            "unknown",
                        );

                        placeHolderAssetPoint.properties.assetName = asset.name; // RAV-5475: To show asset's name in the cluster. I think this is better than looking up the asset name from the props.assets array in a loop when rendering the cluster

                        features.push(placeHolderAssetPoint);
                    }
                    else{
                        features.push(assetFeature);
                    }
                }
            });

            // If clustering is enabled, group features by distance and generate a cluster feature for each group
            // And return the clustered features instead of the original features            
            if(cluster){
                
                const clusteredFeatures = this.enableClustering(features);                
                
                const shouldUseMarkers = this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_ASSET;
                
                // If selected resource is an asset, add markers to the map directly (which covers vehicles pins)
                if(shouldUseMarkers){
                    const clusterMarkers = this.generateClustersMarkers(clusteredFeatures);
                    this.setFiltersForSelectedAssetsOutOfClusters(clusteredFeatures);
                    this.updateClustersMarkersOnTheMap(clusterMarkers);
                }
                // Else, use markers for the clusters pins on assets layer
                else{
                    this.clearClustersMarkersOnTheMap();
                    this.setFiltersForSelectedAssetsOutOfClusters(clusteredFeatures);
                    clusteredFeatures.forEach(feature => {
                        feature.properties.showClusterInLayer = true;
                        const key = feature.id + "-" + (feature.properties.containsTheSelectedResource ? "selected" : "normal");
                        
                        if (!this.assetTrackerImages[feature.id] || this.assetTrackerImages[feature.id] !== key) {
                            const marker = this.createClusterMarkerImage(feature)
                            MapboxHelper.addClusterImagesToMap(this.map, feature.id, marker);
                            this.assetTrackerImages[feature.id] = key;
                        }
                    });
                }

                return [clusteredFeatures, selectedFeature];
            }
        }

        // return the original features if there are no assets or clustering is disabled        
        return [features, selectedFeature];
    }

    // Groups features by distance and generates a cluster feature for each group
    enableClustering = (features) => {
        if(features.length === 0) return features;

        if(!this.maxZoomClusterMarkersGenerated){
            this.generateMaxZoomClusterMarkers(features);
            this.maxZoomClusterMarkersGenerated = true;
        }

        if(!this.currentClusterRadius) {
            this.currentClusterRadius = this.calculateClusterRadius(this.map.getZoom());
        }

        const featuresGroupedByDistance = MapboxHelper.groupFeaturesByDistance(features, this.currentClusterRadius);
        
        return featuresGroupedByDistance.map((group, index) => this.generateClusterFeature(index, group));

    }

    generateMaxZoomClusterMarkers = (features) => {
        const featuresGroupedByDistance = MapboxHelper.groupFeaturesByDistance(features, this.calculateClusterRadius(this.map.getMaxZoom()));
        this.assetsClustersForMaxZoom = featuresGroupedByDistance.map((group, index) => this.generateClusterFeature(index, group));
    }

    // Check the zoom level and update clusters if needed
    updateClusters = () => {
        if(!this.props.assets || !this.props.assets.length || this.props.assets.length <= 0 ) return;

        const zoomLevel = this.map.getZoom();

        const clusterRadius = this.calculateClusterRadius(zoomLevel);

        if(!this.currentClusterRadius) {            
            this.currentClusterRadius = clusterRadius;
        }

        if(this.currentClusterRadius === clusterRadius) {
            return;
        }

        this.currentClusterRadius = clusterRadius;

        let selectedAssetsFeatureCollection;

        // assets
        const [assetsFeatures, selectedAssetFeature] = this.generateAssetsFeatures();
        
        const assetsFeatureCollection = {
            'type': 'FeatureCollection',
            'features': assetsFeatures
        };
        
        if(selectedAssetFeature){
            selectedAssetsFeatureCollection = {
            'type': 'FeatureCollection',
            'features': [selectedAssetFeature]
            };
        }

        // vehicles
        // Not using clustering now. 
        // If needed, we can generate vehicle features here and pass it to forceUpdateOfMapboxData()

        this.mapboxDataLastUpdate = Date.now();
        this.forceUpdateOfMapboxData(
            undefined,
            selectedAssetsFeatureCollection,
            undefined,
            undefined,
            assetsFeatureCollection,
        );
    }

    // Based on the grouping of features, generate a cluster feature for each group (check enableClustering() method line 1872 )
    generateClusterFeature = (index, features) => {

        // if there is just one feature in the group, return it as it is. It's a single point, not a cluster
        if(features.length === 1) {
            return features[0];
        }

        const abbreviatedCount = features.length >= 1000 ? `${(features.length / 1000).toFixed(1)}K` : features.length;
        const containsTheSelectedResource = features.some(feature => feature.properties.ravenUuid === this.props.selectedResource.resourceId);
        const cluster_id = this.generateClusterUniqueIdFromAssets(features); // a join of all the asset ids in the cluster
        const hasSubClusters = this.hasSubClusters(features);

        const feature = {
            id: cluster_id,
            type: 'Feature',
            properties: {
                cluster: features.length > 1,
                cluster_id: cluster_id,
                point_count: features.length,
                point_count_abbreviated: abbreviatedCount,
                leaves:features, // list of all the features in the cluster, using it to create the list in the cluster
                containsTheSelectedResource, // flag to highlight the cluster if it contains the selected asset
                hasSubClusters, // flag to show the stem and base
            },
            geometry: {
                type: 'Point',
                coordinates: MapboxHelper.getCenterOfFeatures(features)
            }
        }

        feature.properties[MapboxHelper.keyForVehicleMarker()] = MapboxHelper.imageAssetNameForRavenUuid(cluster_id);
        
        return feature;
    }

    hasSubClusters = (clusterFeatures) => {
        if(!this.assetsClustersForMaxZoom){
            return false;
        }

        for(let i = 0; i < this.assetsClustersForMaxZoom.length; i++){
            const maxZoomCluster = this.assetsClustersForMaxZoom[i];

            if(!maxZoomCluster.properties.cluster){ // single 
                if(clusterFeatures.some(leaf => leaf.properties.ravenUuid === maxZoomCluster.properties.ravenUuid))
                {
                    return true;
                }
            }
            else{
                if(clusterFeatures.length > maxZoomCluster.properties.leaves.length &&
                    maxZoomCluster.properties.leaves.every(leaf => clusterFeatures.some(feature => feature.properties.ravenUuid === leaf.properties.ravenUuid))){
                    return true;
                }
            }
        }

        return false;
    }

    generatePointFeature = (index, raven) =>
    {
        var hidden = raven.hidden;
        if(!hidden) hidden = false;

        var ravenUuid = raven.ravenUuid;

        // Create the geojson data

        // POINT data
        var vehicleStatus = raven.isDriving ? "driving" : "parked";

        var showProblemsBadge = raven.vehicle.activeDTCs.length > 0;

        var coordinates = [
            raven.lastLocation.lon,
            raven.lastLocation.lat 
        ];

        var hasMultipleOccupants = raven.vehicle.hasPassengers; 

        return MapboxHelper.pointTypeFeatureForRavensCurrentPosition (
                coordinates,
                index,
                ravenUuid,
                vehicleStatus,
                showProblemsBadge,
                hasMultipleOccupants
        );
    }


    //#region updating map with data
    forceUpdateOfMapboxData = (mapboxFeatureCollectionForAllRavensVehiclesPoints, 
            mapboxFeatureCollectionForSelectedRavenVehiclesPoints,
            mapboxFeatureCollectionForAllRavensEventsPoints, 
            mapboxFeatureCollectionForAllRavensRoutes,
            mapboxFeatureCollectionForAllAssets
    ) => {

        const assets = this.props.assets;

        if (this.mapLoaded === true) {  // check if map's style is completely loaded
            if(mapboxFeatureCollectionForAllRavensVehiclesPoints) {
                if (!this.map.getSource(SOURCE_RAVEN_VEHICLE_POINTS_NAME)) {
                    this.addEmptySourcesAndLayers(this.map);
                }
                this.map.getSource(SOURCE_RAVEN_VEHICLE_POINTS_NAME).setData(mapboxFeatureCollectionForAllRavensVehiclesPoints);
            }
            if(mapboxFeatureCollectionForAllAssets) {
                if (!this.map.getSource(SOURCE_ASSETS_NAME)) {
                    this.addEmptySourcesAndLayers(this.map);
                }
                this.map.getSource(SOURCE_ASSETS_NAME).setData(mapboxFeatureCollectionForAllAssets);
            }
            if(mapboxFeatureCollectionForSelectedRavenVehiclesPoints) {
                if (!this.map.getSource(SOURCE_SELECTED_RAVEN_VEHICLE_POINTS_NAME)) {
                    this.addEmptySourcesAndLayers(this.map);
                }
                this.map.getSource(SOURCE_SELECTED_RAVEN_VEHICLE_POINTS_NAME).setData(mapboxFeatureCollectionForSelectedRavenVehiclesPoints);
            }
            if(mapboxFeatureCollectionForAllRavensEventsPoints) {
                if (!this.map.getSource(SOURCE_RAVEN_EVENTS_NAME)) {
                    this.addEmptySourcesAndLayers(this.map);
                }
                this.map.getSource(SOURCE_RAVEN_EVENTS_NAME).setData(mapboxFeatureCollectionForAllRavensEventsPoints);
            }
            if(mapboxFeatureCollectionForAllRavensRoutes) {
                if (!this.map.getSource(SOURCE_RAVEN_ROUTE_NAME)) {
                    this.addEmptySourcesAndLayers(this.map);
                }
                this.map.getSource(SOURCE_RAVEN_ROUTE_NAME ).setData(mapboxFeatureCollectionForAllRavensRoutes);
            }

        }

        // check if map position (bounds/zoom) should be updated to reflect data changes

        // should follow car update
        if (this.mapboxControlIsFollowingSelectedRaven === true) {
            if(mapboxFeatureCollectionForAllRavensVehiclesPoints){ // forceUpdateOfMapboxData() can be called by updateClusters function (which is called by zoom event) to only update assets pins and clusters (without updating the vehicle points)
                var selectedRavenUuid = this.getSelectedRavenUuidFromProps();
                if (selectedRavenUuid) {
                    this.mapboxEaseToRaven(selectedRavenUuid);
                }
            }            
        }

        // is there a new raven, and should we zoom-to-fit it:
        if (this.mapboxUserHasMovedMap === false) {

            if (this.mapboxBoundsSetToFitAllRavensRoutes === false) {
                var boundsForAllRavensRoutes = new mapboxgl.LngLatBounds();
                for ( let i = 0 ; i < this.props.ravens.length; i++)
                {

                    if(this.props.ravens[i].lastLocation) {

                        var coordinates = [
                            this.props.ravens[i].lastLocation.lon,
                            this.props.ravens[i].lastLocation.lat
                        ];

                        boundsForAllRavensRoutes.extend(coordinates);

                        if (this.props.ravens[i].route) {
                            var boundsForRavenRoute = this.props.ravens[i].route.bounds;
                            if (boundsForRavenRoute) {
                                boundsForAllRavensRoutes.extend(boundsForRavenRoute);
                            }
                        }
                    }
                }

                if (assets && assets.length) {
                    // Collect up the asset locations for zooming the map on first load
                    assets.filter(asset => asset.latitude && asset.longitude).forEach(asset => boundsForAllRavensRoutes.extend(new mapboxgl.LngLat(asset.longitude, asset.latitude)));
                }

                if (!boundsForAllRavensRoutes.isEmpty()) {
                    this.mapboxBoundsSetToFitAllRavensRoutes = true;
                    //console.log("this.mapboxBoundsForAllRavensRoutes", this.mapboxBoundsForAllRavensRoutes);
                    this.map.fitBounds(boundsForAllRavensRoutes, {padding: 100});
                }
            }
        }

        
        this.setLayerOrders();
    }
    //#endregion

    setLayerOrders = () => {
        if(!this.mapLoaded) return;

        const selectedResource = this.props.selectedResource.resourceType;
        const activeMenu = this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_ASSET ? "asset" :
                                 this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_VEHICLE ? "vehicle": 
                                 null;

        if(!activeMenu) return;

        const [layerToMoveUp, layerToMoveDown] = activeMenu === "vehicle" ? [LAYER_RAVEN_VEHICLE_POINTS_NAME, LAYER_ASSETS_NAME] : [LAYER_ASSETS_NAME, LAYER_RAVEN_VEHICLE_POINTS_NAME];
        
        if(selectedResource === activeMenu){
            this.map.moveLayer(LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME, null);
            this.map.moveLayer(layerToMoveUp, LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME);
            this.map.moveLayer(layerToMoveDown, layerToMoveUp);
        }
        else{
            this.map.moveLayer(layerToMoveUp, null);
            this.map.moveLayer(LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME, layerToMoveUp);
            this.map.moveLayer(layerToMoveDown, LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME);
        }
    }

    mapboxDataUpdateThrottled = (force = false) => {

        // if the timeout already exists, clear it (effectively: Don't update the map while there is a burst of update requests)
        if (this.mapboxDataUpdateThrottlerTimeout) {

            if(force)
            {
                // If we are forcing, don't reset the timeout already in progress
                return;
            }

            clearTimeout(this.mapboxDataUpdateThrottlerTimeout);

        }

        this.mapboxDataUpdateThrottlerTimeout = setTimeout(() => {

            this.mapboxDataUpdateThrottlerTimeout = null;

            this.mapboxDataLastUpdate = Date.now();

            this.setMapSourceFromRavens();

        // this.forceUpdateOfMapboxData(...); will execute at a max rate of 10fps
        }, 100); // update the map within a 10/th of a second
    }

    shouldComponentUpdate(nextProps) {
        return true;

        const { resourceType, resourceId } = this.props.selectedResource;
        return this.props.updateKey !== nextProps.updateKey
            || resourceType !== nextProps.selectedResource.resourceType
            || resourceId !== nextProps.selectedResource.resourceId
            || this.props.detailsPanelIsOpen !== nextProps.detailsPanelIsOpen
            || this.props.geofenceGeometryEditModeActive !== nextProps.geofenceGeometryEditModeActive;
    }

    hasAssetLocationChanged = (prevProps, assetUuid) => {
        const asset = this.props.assets.find(asset => asset.assetUuid === assetUuid);
        if (!asset) return false;

        const prevAsset = prevProps.assets.find(asset => asset.assetUuid === assetUuid);
        if (!prevAsset) return false;
        
        return asset.latitude !== prevAsset.latitude || asset.longitude !== prevAsset.longitude;
    }

    hasAssetsBeenLoaded = (prevProps) => {
        return prevProps.assets.length === 0 && this.props.assets.length > 0;
    }

    componentDidUpdate(prevProps) {
        const { selectedResource: sel } = this.props;
        const { selectedResource: prevSel } = prevProps;

        if (sel.resourceType === 'geofence' && sel.resourceId
            && (sel.resourceType !== prevSel.resourceType || sel.resourceId !== prevSel.resourceId)) {
            this.easeToGeofence(sel.resourceId);
        }

        if (sel.resourceType === 'asset' && 
            sel.resourceId &&
            (sel.resourceType !== prevSel.resourceType || 
                sel.resourceId !== prevSel.resourceId ||
                this.hasAssetsBeenLoaded(prevProps) || // RAV-5541 When assets are loaded late, the map has already been loaded and without this check, the easing to the selected asset doesn't happen because they're not yet loaded on the map
                this.hasAssetLocationChanged(prevProps, sel.resourceId))) {
            this.easeToAsset(sel.resourceId);
        }

        if (this.props.geofenceGeometryEditModeActive !== prevProps.geofenceGeometryEditModeActive) {
            const mapContainer = document.getElementById(this.props.mapId);
            if (!prevProps.geofenceGeometryEditModeActive) {
                if (this.mapboxToggleFullscreenControl) {
                    this.mapboxRemoveToggleFullscreenControl();
                    if (mapContainer) {
                        mapContainer.removeEventListener("keydown", this.onKeyDown, false);
                    }
                }
            } else {
                if (!this.mapboxToggleFullscreenControl) {
                    this.mapboxAddToggleFullscreenControl();
                    if (mapContainer) {
                        mapContainer.addEventListener("keydown", this.onKeyDown, false);
                    }
                }
            }
        }

        var selectedRavenUuid = this.getSelectedRavenUuidFromProps();
        var selectedassetUuid = this.getSelectedassetUuid();

        this.mapboxDataUpdateThrottled(this.mapSelectedRavenUuid !== selectedRavenUuid);

        if(this.state.vehiclePopup)
        {
            this.showVehiclePopupForRavenUuid (this.state.vehiclePopup.ravenUuid, this.state.vehiclePopup.coordinates, this.state.vehiclePopup.addToTarget);
        }
    }

    resize = () => this.map.resize()

    render() {
        const { resourceType, resourceId } = this.props.selectedResource;
        const { geofenceGeometryEditModeActive } = this.props;

        const editingGeofence = resourceType === 'geofence' && geofenceGeometryEditModeActive;
        const editingCircleGeofence = editingGeofence && this.props.geofenceType(resourceId) === "Point";
        const editingPolygonGeofence = editingGeofence && this.props.geofenceType(resourceId) === "Polygon";

        return (
            <div className="mapbox-container">
                <ResizeThrottler interval={300} callback={this.resize} />
                <BackgroundRefresher tripSharing={false} />
                <div id={this.props.mapId} className="mapbox-map mapboxgl-ctrl">
                    {this.map && editingCircleGeofence ?
                        <GeofenceEditor
                            mapboxMap = {this.map}
                            detailsPanelIsOpen = {this.props.detailsPanelIsOpen}
                            geofenceId={resourceId}
                            stage={this.props.user && this.props.user.stage}
                            setGeofenceLayersVisible={this.setGeofenceLayersVisible}
                        />
                        : editingPolygonGeofence ?
                        // Todo : Move the whole Polygon drawing logic to this component (like what geofenceEditor.js does for circle geofences)
                        <GeofenceEditorBackground geofenceId={resourceId} mapboxMap={this.map} setGeofenceLayersVisible={this.setGeofenceLayersVisible}  />
                        :null
                    }
                </div>
            </div>
        );
    }

    updateMapboxRouteLineFeatureCollectionForRaven = (raven) => {

        var updatedFeatureCollection = {
            'type': 'FeatureCollection',
            'features': []
        };


        if (!raven.ravenUuid) {
            return updatedFeatureCollection ;
        }

        if (raven.isHidden === true) {
            return updatedFeatureCollection ;
        }

        if (!raven.dashboard) {
            return updatedFeatureCollection ;
        }

        var vehicleStatus = raven.isDriving ? "driving" : "parked";

        var telematicsArrayOfTimeLonLatArrays = [];
        var timestamps = [];

        for (var i = 0; i < raven.dashboard.route.length; i++) {

            var routeUnixTimestamp;
            if (isNaN(raven.dashboard.route[i].ts)) { // assumption: it is a data string (Kloud response) and it needs to be parsed
                routeUnixTimestamp = Math.round(Date.parse(raven.dashboard.route[i].ts) / 1000);
            } else {
                routeUnixTimestamp = Math.round(raven.dashboard.route[i].ts / 1000); // it is a millisecond timestamp (mqtt packet) and it just needs to be converted to seconds timestamp (a.k.a. unix timestamp)
            }
            var routeLon = raven.dashboard.route[i].lon;
            var routeLat = raven.dashboard.route[i].lat; //OPTIMIZE_GPS_DATA

            if (isNaN(routeUnixTimestamp)) {
                continue;
            }
            if (OPTIMIZE_GPS_DATA === true) {
                routeLon = TelematicsHelper.optimizedDecimalPlaces(routeLon);
                routeLat = TelematicsHelper.optimizedDecimalPlaces(routeLat);
            }
            telematicsArrayOfTimeLonLatArrays.push([ //array [<unix_timestamp>, <lat>, <lon>] (<unix_timestamp> first for easy sorting purposes)
                routeUnixTimestamp,
                routeLon,
                routeLat
            ]);

            timestamps.push(raven.dashboard.route[i].ts);

            if (this.mapboxUserHasMovedMap === false) { // avoid spending CPU extending the bounds if the user has already moved the map (therefore, zoom to fit bounds will not be executed anyway)
                // extend bounds for the route line
                var lngLat = new mapboxgl.LngLat(routeLon, routeLat);
                // boundsToFitRavenRoute.extend(lngLat);
            }

        }

        var uniqId = 1;

        updatedFeatureCollection.features.push(
            MapboxHelper.lineStringTypeFeatureForAscendingSortOrderArrayOfTelematicsTimeLonLatArrays(
                telematicsArrayOfTimeLonLatArrays,
                uniqId,
                raven.ravenUuid,
                raven.canViewVideoStream,
                vehicleStatus,
                timestamps
            )
        );

        return updatedFeatureCollection;
    }

    mapboxAddToggleFullscreenControl = () => {
        this.mapboxToggleFullscreenControl = new MapboxToggleFullscreenControl(
            this.mapboxControlShouldFullscreen,
            this.mapboxToggleFullscreenControlCallback
        );
        this.map.addControl(this.mapboxToggleFullscreenControl);
    }

    mapboxRemoveToggleFullscreenControl = () => {
        this.map.removeControl(this.mapboxToggleFullscreenControl);
        this.mapboxToggleFullscreenControl = null;
    }

    mapboxToggleFullscreenControlCallback = (shouldFullscreen) => {

        this.mapboxControlShouldFullscreen = shouldFullscreen;
        this.mapboxToggleFullscreenControl.mapboxControlShouldFullscreen = this.mapboxControlShouldFullscreen; // this is redundant when mapboxToggleFullscreenControl calls mapboxToggleFullscreenControlCallback

        const documentElement = document.documentElement; // Get the documentElement (<html>) to display the page in fullscreen (https://www.w3schools.com/howto/howto_js_fullscreen.asp)

        if ((fscreen.fullscreenEnabled === false) || // 'fullscreenEnabled' means native browser fullscreen is supported/enabled (doesn't mean it's actively fullscreened)
            !documentElement) { // assuming an undefined documentElement is bad enough that we should skip a native fullscreen attempt
            this.props.setLayoutFullscreen(this.mapboxControlShouldFullscreen);
            return;
        }

        if (this.mapboxControlShouldFullscreen) {
            if (fscreen.fullscreenElement === null) { // checking fullscreen mode is not already active
                fscreen.requestFullscreen(documentElement);
            }
        } else {
            if (fscreen.fullscreenElement !== null) { // checking fullscreen mode is still active
                fscreen.exitFullscreen();
            }
        }
    }

    onFullscreenChange = (event) => { // https://developer.mozilla.org/en-US/docs/Web/API/Document/fullscreenchange_event
        
        if (fscreen.fullscreenElement !== null) {
            this.mapboxControlShouldFullscreen = true;
        } else {
            this.mapboxControlShouldFullscreen = false;
        }
        this.mapboxToggleFullscreenControl.mapboxControlShouldFullscreen = this.mapboxControlShouldFullscreen;
        this.props.setLayoutFullscreen(this.mapboxControlShouldFullscreen);                            
    }

    updateMapboxPointFeatureCollectionForRavenEvents = (raven) => {

        var updatedFeatureCollection = {
            'type': 'FeatureCollection',
            'features': []
        };

        if (!raven.ravenUuid) {
            return updatedFeatureCollection ;
        }

        if (raven.trailsHidden === true) {
            return updatedFeatureCollection ;
        }

        if (raven.isHidden === true) {
            return updatedFeatureCollection ;
        }

        if (!raven.dashboard) {
            return updatedFeatureCollection ;
        }

        var eventsTimeLonLatNameCategoryIdFeatureIdArrays = [];

        for (var j = 0; j < raven.dashboard.events.length; j++) {

            if (!raven.dashboard.events[j].geoStamp) {
                continue;
            }

            var unixTimestamp = Math.round(Date.parse(raven.dashboard.events[j].ts) / 1000);
            //console.log("NEW raven.dashboard.events[j].geoStamp.lat:", raven.dashboard.events[j].geoStamp.lat);
            var lonRaw = raven.dashboard.events[j].geoStamp.lon;
            var latRaw = raven.dashboard.events[j].geoStamp.lat; //OPTIMIZE_GPS_DATA
            var eventTypeProtobufString = raven.dashboard.events[j].type;
            var eventCategoryNameString = EVENT_CATEGORY_NAMES.FOR_EVENT_TYPE(eventTypeProtobufString);

            var eventTimeLonLatNameCategoryNameArrayItem;

            if (OPTIMIZE_GPS_DATA === true) {
                eventTimeLonLatNameCategoryNameArrayItem = [
                    unixTimestamp,
                    TelematicsHelper.optimizedDecimalPlaces(lonRaw),
                    TelematicsHelper.optimizedDecimalPlaces(latRaw),
                    eventTypeProtobufString,
                    eventCategoryNameString
                ];
            } else {
                eventTimeLonLatNameCategoryNameArrayItem = [
                    unixTimestamp,
                    lonRaw,
                    latRaw,
                    eventTypeProtobufString,
                    eventCategoryNameString
                ];
            }

            var eventCategoryNameString = EVENT_CATEGORY_NAMES.FOR_EVENT_TYPE(eventTypeProtobufString);
            var eventCategoryIdAsString = "" + eventCategoryNameString;

            updatedFeatureCollection.features.push(
                MapboxHelper.pointTypeFeatureForRavenEvent(
                    [eventTimeLonLatNameCategoryNameArrayItem [1], eventTimeLonLatNameCategoryNameArrayItem [2]],
                    eventTimeLonLatNameCategoryNameArrayItem[5] ,
                    eventCategoryNameString,
                    raven.ravenUuid
                )
            );

        }

        return updatedFeatureCollection;
    }

    setSelectedFeatureStateForAsset = (hoveredStateBool, assetUuid) => {
        
        if(!assetUuid) return;
        // FIXME - Update picture of currently selected Raven, and newly selected Raven
        this.map.setFeatureState({source: SOURCE_ASSETS_NAME, id: assetUuid}, { hoverOrSelected: hoveredStateBool});
    }

    setHoveredFeatureStateForRavenUuid = (hoveredStateBool, ravenUuid) => {

        if(!this.mapDataModel.ravenData[ravenUuid])
            return;

        var uniqId = this.mapDataModel.ravenData[ravenUuid].id;

        this.map.setFeatureState({source: SOURCE_RAVEN_ROUTE_NAME, id: uniqId}, { hoverOrSelected: hoveredStateBool});
        this.map.setFeatureState({source: SOURCE_RAVEN_VEHICLE_POINTS_NAME, id: uniqId}, { hoverOrSelected: hoveredStateBool});
        this.map.setFeatureState({source: SOURCE_RAVEN_EVENTS_NAME, id: uniqId}, { hoverOrSelected: hoveredStateBool});
    }

    setTrailsHiddenFeatureStateForRavenUuid = (isHiddenStateBool, ravenUuid) => {


    }


    getLastPosition = (ravenUuid) => {

        if (!ravenUuid) {
            return null;
        }

        var targetRaven = this.getRavenByUuid(ravenUuid);

        var lastTimeLonLatArrayForSelectedRaven = [ null, targetRaven.lastLocation.lon, targetRaven.lastLocation.lat ];

        return [
            lastTimeLonLatArrayForSelectedRaven[1], // lon
            lastTimeLonLatArrayForSelectedRaven[2] // lat
        ];
    }


    easeToGeofence = (id) => {

        const geojson = this.props.geofenceGeojson(id);
        if (!geojson) return;

        const { coordinates } = geojson.geometry || {};
        if (!coordinates) return;

        const geofenceCenter = geojson.geometry.type === "Polygon" ? MapboxHelper.getCenterOfPolygon(coordinates) : coordinates;
        
        this.map.easeTo({
            center:  geofenceCenter,
            easing: MapboxHelper.easingFunctions.easeInOutQuad,
            
        });
    }

    easeToAsset = (id) => {

        const asset = this.props.assets.find(asset => asset.assetUuid === id);
        
        if(!asset) return;

        const { latitude, longitude } = asset;

        if (!latitude || !longitude) return;

        const zoomLevel = this.map.getZoom();       
        
        let duration = 2000;

        if(zoomLevel <= 14)
            duration = 3000;

        if(zoomLevel <= 7)
            duration = 5000;

        if(zoomLevel <= 3)
            duration = 6000;

        
        this.map.easeTo({
            center:  [longitude, latitude],
            easing: MapboxHelper.easingFunctions.easeInOutQuad,
        });

        this.map.flyTo({
            center:  [longitude, latitude],
            zoom: this.map.getMaxZoom(),
            duration: duration
        });
    }

    setGeofenceLayersVisible = visible => {

        const { geofenceGeometryEditModeActive } = this.props;

        // if just switching between geofence types, don't change visibility
        if(visible && geofenceGeometryEditModeActive)
            return;

        this.geofenceLayersVisible = visible;

        MapboxHelper.setGeofenceLayersVisible(this.map, visible);
    }

    generateClustersMarkers(features) {
        const newMarkers = {};
        
        // for every cluster, create an HTML marker for it (if we didn't already),
        // and add it to the map if it's not there already
        for (const feature of features) {
            // If it's not a cluster, i.e. it's a single asset point outside the range of any cluster
            if (!feature.properties.cluster) {
                // skip the process
                continue;
            }


            const clusterCoords = feature.geometry.coordinates;
            const clusterProps = feature.properties;
            const clusterId = clusterProps.cluster_id;
            const assetsInTheCluster = clusterProps.leaves;
            const markerIncludesSelectedAsset = clusterProps.containsTheSelectedResource;
            const hasSubClusters = clusterProps.hasSubClusters;

            let isCoveringSelectedResource = false;
            let statusRelativeToSelectedResource;

            // If a marker is already created for this cluster (based on the id), get it
            let marker = this.assetClustersMarkers[clusterId];

            // If the selected resource is not asset (a vehicle), we need to check if the cluster is covering the selected vehicle
            // And if so, change marker's props (anchor, direction)
            if(this.props.selectedResource.resourceType !== "asset" && this.mapLoaded) {
                const ravenFeature = this.map.queryRenderedFeatures({ layers: [LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME] })[0];
                // get the distance and position of the cluster relative to the selected vehicle
                statusRelativeToSelectedResource = MapboxHelper.getDistanceAndPositionBetweenTwoFeatures(this.map, feature, ravenFeature);

                if(statusRelativeToSelectedResource){
                    // check if the cluster is covering the selected vehicle based on the distance and position 
                    isCoveringSelectedResource = MapboxHelper.isClusterCoveringAFeature(statusRelativeToSelectedResource);
                }
            }
            
            // We need to update marker if
            const shouldUpdateMarker = !marker ||                                                          // - it's not created yet
                                        isCoveringSelectedResource ||                                      // - it's covering the selected resource
                                        marker.isCoveringSelectedResource !== isCoveringSelectedResource;  // - it's not covering the selected resource and it was covering it before
            
            if (shouldUpdateMarker) {

                // Find the proper anchor position for the cluster marker based on whether it's covering the selected resource
                const markerAnchorPosition = this.getClusterMarkerAnchor(isCoveringSelectedResource, statusRelativeToSelectedResource);

                const markerProps = {
                    ...clusterProps,
                    isCoveringAPin: isCoveringSelectedResource,
                    status: statusRelativeToSelectedResource,
                    anchorPosition: markerAnchorPosition,
                    showStemAndBase: !hasSubClusters // show stem and base If this cluster doesn't have sub clusters (It's not a cluster covering 2 or more clusters of greater zoom levels)
                }

                let markerElement;
                
                if(!marker){
                    markerElement = this.createClusterMarkerElement(markerProps, assetsInTheCluster);
                }

                // If the marker is already created But its covering status has changed, remove it from the map and append the new one
                if(marker && (isCoveringSelectedResource  || marker.isCoveringSelectedResource !== isCoveringSelectedResource))
                {
                    markerElement = this.updateClusterMarkerElement(marker.getElement(), markerProps);
                }

                // we creating a new marker so remove the old one if there is one
                if(this.assetClustersMarkersOnScreen[clusterId]){
                    this.assetClustersMarkersOnScreen[clusterId].remove();
                    delete this.assetClustersMarkersOnScreen[clusterId];
                }

                // create new marker with new content
                marker = new mapboxgl.Marker({
                    element: markerElement,
                    anchor: markerAnchorPosition
                }).setLngLat(clusterCoords);
                
                marker.uuid = clusterId;
                marker.isCoveringSelectedResource = isCoveringSelectedResource;

                // save it so we can:
                // use it later without recreating it
                // remove it later if it's no longer visible
                this.assetClustersMarkers[clusterId] = marker;
            }

            
            marker.includesSelectedAsset = markerIncludesSelectedAsset;

            // set cluster marker style when map is loading or switching from images to markers
            // To set style based on cluster is selected or not AND if it includes the selected asset
            this.setClusterMarkerStyle(marker);

            // Use this object to keep track of the markers that are currently on the screen
            newMarkers[clusterId] = marker;            
        }

        return newMarkers;
    }

    isSelectedResourceInASingleCluster = (features) => {
        const selectedResourceId = this.props.selectedResource.resourceId;
        return features.some(feature => feature.id === selectedResourceId);
    }

    updateClustersMarkersOnTheMap = (newMarkers) => {

        for (const clusterId in newMarkers) {
            const marker = newMarkers[clusterId];
            // add the marker to the map if it's not there already
            if (!this.assetClustersMarkersOnScreen[clusterId]) {
                marker.addTo(this.map);
            }
        }

        // for every marker we've added previously, remove those that are no longer visible
        for (const clusterId in this.assetClustersMarkersOnScreen) {
            if (!newMarkers[clusterId]){
                this.assetClustersMarkersOnScreen[clusterId].remove();
            }                
        }
        
        // update the object that keeps track of the markers that are currently on the screen
        this.assetClustersMarkersOnScreen = newMarkers;
    }

    clearClustersMarkersOnTheMap = () => {        
        for (const clusterId in this.assetClustersMarkersOnScreen) {
            this.assetClustersMarkersOnScreen[clusterId].remove();
            delete this.assetClustersMarkersOnScreen[clusterId];
        }
    }

    generateClusterUniqueIdFromAssets = (assets) => {
        return assets.map(asset => asset.properties.ravenUuid).sort().join('_');
    }

    setFiltersForSelectedAssetsOutOfClusters = (features) => {
        if(!this.mapLoaded) return;

        // No need to set the filter if the selected resource is not an asset
         if(this.props.selectedResource.resourceType !== "asset") return;

         const allowedIds = [];

        if(this.isSelectedResourceInASingleCluster(features)){
            allowedIds.push(this.props.selectedResource.resourceId);
        }

         // Show the selected asset feature if it's one of the assets out of the range of any cluster
         this.map.setFilter(LAYER_SELECTED_RAVEN_VEHICLE_POINTS_NAME, [
            'case',
            ["==", ['get', 'cluster'], true], false,
            ['==', ['get', 'markerCategory'], 'vehicle'], true,
            ['==', ['get', 'markerCategory'], 'asset'], ['in', ['get', 'ravenUuid'], ['literal', allowedIds]],
            false
        ]);

    }

    setClusterMarkerStyle = (marker) => {
        const markerElement = marker.getElement();

        const includesSelectedAsset = marker.includesSelectedAsset;
        const isSelected = this.IdOfclusterImageClickedByUser !== undefined ? this.IdOfclusterImageClickedByUser === marker.uuid : markerElement.classList.contains(ASSETS_CLUSTERS_CLASSES.SELECTED);

        const markerBackgroundColor = includesSelectedAsset || isSelected ? ASSETS_CLUSTERS_COLORS.SELECTED_CLUSTER_BACKGROUND : ASSETS_CLUSTERS_COLORS.DEFAULT_CLUSTER_BACKGROUND;

        // Main background of the cluster marker
        const outerWrapper = markerElement.querySelector(`.${ASSETS_CLUSTERS_CLASSES.CLUSTER_WRAPPER}`);
        outerWrapper.style.backgroundColor = markerBackgroundColor;
        outerWrapper.style.borderColor = ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER;

        // SET selected class if user has clicked on the cluster (but not a single asset)
        if(isSelected){
            markerElement.classList.add(ASSETS_CLUSTERS_CLASSES.SELECTED);
            outerWrapper.classList.add(ASSETS_CLUSTERS_CLASSES.SELECTED);
        } else {
            markerElement.classList.remove(ASSETS_CLUSTERS_CLASSES.SELECTED);
            outerWrapper.classList.remove(ASSETS_CLUSTERS_CLASSES.SELECTED);
        }

        // InnerWrapper is the container for the assets list (allows scrolling)
        const innerWrapper = markerElement.querySelector(`.${ASSETS_CLUSTERS_CLASSES.LIST_WRAPPER}`);
        innerWrapper.style.scrollbarColor = `${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER} ${markerBackgroundColor}`;

        //remove the highlight from the previously selected asset
        const prevSelectedAssetBgRect = markerElement.querySelector(`.${ASSETS_CLUSTERS_CLASSES.ASSET_BACKGROUND_RECT}.${ASSETS_CLUSTERS_CLASSES.SELECTED}`);
        const prevSelectedAssetText = markerElement.querySelector(`.${ASSETS_CLUSTERS_CLASSES.ASSET_TEXT}.${ASSETS_CLUSTERS_CLASSES.SELECTED}`);
        
        if(prevSelectedAssetBgRect && prevSelectedAssetText){
            prevSelectedAssetBgRect.style.fill = ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_BACKGROUND;
            prevSelectedAssetText.style.fill = ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR;
            
            markerElement.classList.remove(ASSETS_CLUSTERS_CLASSES.CONTAINS_SELECTED_ASSET);
            prevSelectedAssetBgRect.classList.remove(ASSETS_CLUSTERS_CLASSES.SELECTED);
            prevSelectedAssetText.classList.remove(ASSETS_CLUSTERS_CLASSES.SELECTED);
        }

        // If the cluster marker includes the selected asset, find that asset in the list and highlight it
        if(includesSelectedAsset){
            const selectedAssetUuid = this.props.selectedResource.resourceId;

            const selectedAssetBgRect = markerElement.querySelector(`#${ASSETS_CLUSTERS_CLASSES.ASSET_BACKGROUND_RECT}-${selectedAssetUuid}`);
            const selectedAssetText = markerElement.querySelector(`#${ASSETS_CLUSTERS_CLASSES.ASSET_TEXT}-${selectedAssetUuid}`);

            if(selectedAssetBgRect && selectedAssetText){
                markerElement.classList.add(ASSETS_CLUSTERS_CLASSES.CONTAINS_SELECTED_ASSET);

                selectedAssetBgRect.style.fill = ASSETS_CLUSTERS_COLORS.SELECTED_ASSET_BACKGROUND;
                selectedAssetBgRect.classList.add(ASSETS_CLUSTERS_CLASSES.SELECTED);
                selectedAssetText.style.fill = ASSETS_CLUSTERS_COLORS.SELECTED_ASSET_COLOR;
                selectedAssetText.classList.add(ASSETS_CLUSTERS_CLASSES.SELECTED);
            }
        }        
    }

    // code for creating an SVG donut chart from feature properties
    createClusterMarkerElement(props, assetsInTheCluster) {
        
        const label = props.point_count_abbreviated;
        
        const expandable = assetsInTheCluster.length > ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST;

        // asset(s) different heights
        const singleAssetHeight = ASSETS_CLUSTERS_FONT_SIZE;
        const singleAssetTotalHeight = singleAssetHeight + ASSETS_CLUSTERS_SINGLE_ASSET_PADDING * 2; // height + padding top and bottom
        const allAssetsHeight = singleAssetTotalHeight * assetsInTheCluster.length;
        const viewAbleAssetsHeight = singleAssetTotalHeight * (assetsInTheCluster.length > ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST ? ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST + 1 : assetsInTheCluster.length + 1);

        // list
        const paddingsAndBorders = ASSETS_CLUSTERS_LIST_PADDING * 2 + ASSETS_CLUSTERS_LIST_BORDER_WIDTH * 2;
        const listNormalHeight = (assetsInTheCluster.length > ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST ? singleAssetTotalHeight : allAssetsHeight) + paddingsAndBorders; // If number of assets in the cluster is more than the max number to show, show only a title and a down arrow (which can be expanded)
        const listExpandedHeight =  allAssetsHeight + singleAssetTotalHeight + paddingsAndBorders; // One more 'singleAssetTotalHeight' is for the Title and the down arrow; Cuz when expanded, the list will show all assets + the title and the down arrow
        
        // svg wrapping the list of assets
        const headSvgWidth = ASSETS_CLUSTERS_LIST_WIDTH;
        const headSvgViewBox = `0 0 ${headSvgWidth} ${listExpandedHeight}`;

        // rect as list of assets background
        const backgroundRectClass = expandable ? 
                                    ASSETS_CLUSTERS_CLASSES.NORMAL_LIST_BACKGROUND_RECT + ' ' + ASSETS_CLUSTERS_CLASSES.EXPANDABLE_LIST_BACKGROUND_RECT : 
                                    ASSETS_CLUSTERS_CLASSES.NORMAL_LIST_BACKGROUND_RECT;
        const backgroundRectY = 0;
        const backgroundRectX = 0;
        
        // list item rect
        const listItemX = 10;
        const listItemWidth = ASSETS_CLUSTERS_LIST_WIDTH - (listItemX * 2);
        
        // list item text
        const listItemTextX = listItemX * 2;

        // expand/collapse arrow
        const arrowSideLength = 5;
        const arrowStartingX = ASSETS_CLUSTERS_LIST_WIDTH - (listItemTextX + arrowSideLength * 2);
        const arrowStartingY = ASSETS_CLUSTERS_LIST_PADDING / 2;
        const arrowPath = `M${arrowStartingX} Y l${arrowSideLength} ${arrowSideLength} l${arrowSideLength} -${arrowSideLength}`;


        // list item separator
        const listItemSeparatorX1 = listItemX;
        const listItemSeparatorX2 = ASSETS_CLUSTERS_LIST_WIDTH - listItemX;
        const listItemSeparatorY = singleAssetHeight + ASSETS_CLUSTERS_SINGLE_ASSET_PADDING - 1;
        
        // svg wrapping base and stem
        const baseSvgWidth = props.anchorPosition === ASSETS_CLUSTERS_ANCHORS.BOTTOM ? ASSETS_CLUSTERS_LIST_WIDTH : ASSETS_CLUSTERS_BASE_RADIUS * 2 + ASSETS_CLUSTERS_STEM_LONG_LENGTH;
        const baseSvgHeight = props.anchorPosition === ASSETS_CLUSTERS_ANCHORS.BOTTOM ? ASSETS_CLUSTERS_BASE_RADIUS * 2 + ASSETS_CLUSTERS_STEM_LENGTH : singleAssetTotalHeight;
        const baseSvgViewBox = `0 0 ${baseSvgWidth} ${baseSvgHeight}`;

        // circle
        const baseProps = this.getClusterBaseCircleProps(props, baseSvgHeight, baseSvgWidth);

        // line
        const stemProps = this.getClusterStemProps(props, baseSvgHeight, baseSvgWidth);


        // head Part of the marker (list of assets)        
        let headSVG = `<svg class="head-svg" width="${ASSETS_CLUSTERS_LIST_WIDTH}" height="${listExpandedHeight}" viewbox="${headSvgViewBox}" text-anchor="middle" style="font: ${ASSETS_CLUSTERS_FONT_SIZE}px sans-serif; display: block">`;

            // rect as cluster popup background
            headSVG += `<rect class="${backgroundRectClass}" x="${backgroundRectX}" y="${backgroundRectY}" width="${ASSETS_CLUSTERS_LIST_WIDTH}" height="${listNormalHeight}" fill="transparent">`;
                // 2 animate tags to animate the height of the rect when user clicks on the rect. One to expand and one to collapse
                headSVG += `<animate attributeName="height" attributeType="XML" from="${listNormalHeight}" to="${listExpandedHeight}" begin="indefinite" dur="${ASSETS_CLUSTERS_LIST_EXPAND_ANIMATION_DURATION}s" fill="freeze" class="expanded" />`;
                headSVG += `<animate attributeName="height" attributeType="XML" from="${listExpandedHeight}" to="${listNormalHeight}" begin="indefinite" dur="${ASSETS_CLUSTERS_LIST_COLLAPSE_ANIMATION_DURATION}s" fill="freeze" class="collapsed" />`;
            headSVG += `</rect>`;


                let y = ASSETS_CLUSTERS_LIST_PADDING + ASSETS_CLUSTERS_SINGLE_ASSET_PADDING;

            // add an down arrow (not a triangle) to the right of the donut chart
            if(assetsInTheCluster.length > ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST){
                headSVG += `<text dominant-baseline="hanging" text-anchor="start" x="${listItemTextX}" y="${y}" fill="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR}" pointer-events="none">${label} ${strings.resource_group_title_assets}</text>`;
                headSVG += `<path d="${arrowPath.replace('Y', y + arrowStartingY)}" stroke="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR}" fill="transparent" stroke-width="1" pointer-events="none"></path>`;
                
                y += ASSETS_CLUSTERS_LIST_PADDING + singleAssetTotalHeight + 1;
            }

            for (let i = 0; i < assetsInTheCluster.length; i++) {
                const asset = assetsInTheCluster[i];
                const assetName = asset.properties.assetName;
                const assetUuid = asset.properties.ravenUuid;

                headSVG += `<rect id="${ASSETS_CLUSTERS_CLASSES.ASSET_BACKGROUND_RECT}-${assetUuid}" class="${ASSETS_CLUSTERS_CLASSES.ASSET_BACKGROUND_RECT}" data-uuid="${assetUuid}" x="${listItemX}" y="${y - ASSETS_CLUSTERS_SINGLE_ASSET_PADDING - 1}" width="${listItemWidth}" height="${singleAssetTotalHeight}" fill="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_BACKGROUND}" />`;
                headSVG += `<text id="${ASSETS_CLUSTERS_CLASSES.ASSET_TEXT}-${assetUuid}" class="${ASSETS_CLUSTERS_CLASSES.ASSET_TEXT}" data-uuid="${assetUuid}" dominant-baseline="hanging" text-anchor="start" x="${listItemTextX}" y="${y}" fill="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR}" pointer-events="none">${assetName}</text>`;
                
                // add a horizontal line to separate assets
                if(i < assetsInTheCluster.length - 1){
                    headSVG += `<line x1="${listItemSeparatorX1}" y1="${y + listItemSeparatorY}" x2="${listItemSeparatorX2}" y2="${y + listItemSeparatorY}" stroke="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR}" stroke-width="1" stroke-opacity="30%"></line>`;
                }

                y += singleAssetTotalHeight;
            }

        headSVG += `</svg>`;

        // lower Part of the marker (base and stem)
        let baseSVG = '';
        baseSVG += `<svg class="base-svg" width="${baseSvgWidth}" height="${baseSvgHeight}" viewbox="${baseSvgViewBox}" text-anchor="middle" style="font: ${ASSETS_CLUSTERS_FONT_SIZE}px sans-serif; display: block" pointer-events="none">`;
            if(props.showStemAndBase){
                // circle at the top of the marker line
                baseSVG += `<circle cx="${baseProps.cx}" cy="${baseProps.cy}" opacity="${baseProps.opacity}" r="${ASSETS_CLUSTERS_BASE_RADIUS}" fill="${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER}"></circle>`;
            
                // vertical line connecting the base to the cluster popup
                baseSVG += `<line x1="${stemProps.x1}" y1="${stemProps.y1}" x2="${stemProps.x2}" y2="${stemProps.y2}" opacity="${stemProps.opacity}" stroke="${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER}" stroke-width="2"></line>`;
            }
        baseSVG += `</svg>`;


        // wrapper for head
        let html = `<div class="${ASSETS_CLUSTERS_CLASSES.CLUSTER_WRAPPER}" style="height:${listNormalHeight}px; transition: height ${ASSETS_CLUSTERS_LIST_COLLAPSE_ANIMATION_DURATION}s; overflow-y: hidden; background-color: ${ASSETS_CLUSTERS_COLORS.DEFAULT_CLUSTER_BACKGROUND}; border: solid ${ASSETS_CLUSTERS_LIST_BORDER_WIDTH}px ${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER}; border-radius:15px">`;
            html += `<div class="${ASSETS_CLUSTERS_CLASSES.LIST_WRAPPER}" style="height:${listNormalHeight}px; overflow-y: hidden; scrollbar-width: thin; scrollbar-color: ${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER} ${ASSETS_CLUSTERS_COLORS.DEFAULT_CLUSTER_BACKGROUND};">`;
                html += headSVG;
            html += `</div>`;
        html += `</div>`;
         
        // wrapper for stem and base
        html += `<div style="height: max-content">`
            html += baseSVG;
        html += `</div>`;
        
        
        const el = document.createElement('div');
        
        
        el.addEventListener('click', (e) => {
            e.stopPropagation();
            
            this.IdOfclusterImageClickedByUser = undefined; // A cluster is clicked on by user so we can empty this variable which is used when switching from images to markers

            const targetClasses  = [ASSETS_CLUSTERS_CLASSES.NORMAL_LIST_BACKGROUND_RECT, ASSETS_CLUSTERS_CLASSES.EXPANDABLE_LIST_BACKGROUND_RECT, ASSETS_CLUSTERS_CLASSES.ASSET_BACKGROUND_RECT];

            if(!targetClasses.some(className => e.target.classList.contains(className))) return;

            const markerElement = e.target.closest(`.${ASSETS_CLUSTERS_CLASSES.CLUSTER_MARKER}`);
            const outerWrapper = e.target.closest(`.${ASSETS_CLUSTERS_CLASSES.CLUSTER_WRAPPER}`);
            const innerWrapper = e.target.closest(`.${ASSETS_CLUSTERS_CLASSES.LIST_WRAPPER}`);

            // highlight the selected cluster
            if(e.target.classList.contains(ASSETS_CLUSTERS_CLASSES.NORMAL_LIST_BACKGROUND_RECT) ||
                e.target.classList.contains(ASSETS_CLUSTERS_CLASSES.ASSET_BACKGROUND_RECT)){

                // deselect the previously selected cluster marker
                document.querySelectorAll(`.${ASSETS_CLUSTERS_CLASSES.CLUSTER_WRAPPER}.${ASSETS_CLUSTERS_CLASSES.SELECTED}`).forEach(el => {
                    el.classList.toggle(ASSETS_CLUSTERS_CLASSES.SELECTED);
                    el.closest(`.${ASSETS_CLUSTERS_CLASSES.CLUSTER_MARKER}`).classList.remove(ASSETS_CLUSTERS_CLASSES.SELECTED);

                    // un-highlight it Only if it does not contain the selected asset
                    if(!el.closest(`.${ASSETS_CLUSTERS_CLASSES.CLUSTER_MARKER}`).classList.contains(ASSETS_CLUSTERS_CLASSES.CONTAINS_SELECTED_ASSET)){
                        el.style.backgroundColor = ASSETS_CLUSTERS_COLORS.DEFAULT_CLUSTER_BACKGROUND;
                        el.querySelector(`.${ASSETS_CLUSTERS_CLASSES.LIST_WRAPPER}`).style.scrollbarColor = `${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER} ${ASSETS_CLUSTERS_COLORS.DEFAULT_CLUSTER_BACKGROUND}`;
                    }
                });

                // highlight the cluster marker
                markerElement.classList.toggle(ASSETS_CLUSTERS_CLASSES.SELECTED);
                outerWrapper.classList.toggle(ASSETS_CLUSTERS_CLASSES.SELECTED);
                outerWrapper.style.backgroundColor = ASSETS_CLUSTERS_COLORS.SELECTED_CLUSTER_BACKGROUND;
                innerWrapper.style.scrollbarColor = `${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER} ${ASSETS_CLUSTERS_COLORS.SELECTED_CLUSTER_BACKGROUND}`;
            }
            
            // If the user clicks on the Title or the down arrow of the cluster marker
            if(e.target.classList.contains(ASSETS_CLUSTERS_CLASSES.EXPANDABLE_LIST_BACKGROUND_RECT)){
                const bgRect = e.target;
                
                const newState = bgRect.classList.toggle('expanded') ? 'expanded' : 'collapsed';

                // expand/collapse the wrappers                
                outerWrapper.style.height = newState === 'expanded' ? `${viewAbleAssetsHeight}px` : `${listNormalHeight}px`; // If list is longer than the max number of assets to show, a scroll bar will appear
                
                innerWrapper.style.height = newState === 'expanded' ? `${viewAbleAssetsHeight}px` : `${listNormalHeight}px`; // If list is longer than the max number of assets to show, a scroll bar will appear
                innerWrapper.style.overflowY = newState === 'expanded' ? `auto` : `hidden`;
                innerWrapper.scrollTop = 0;
                
                // After we change the height of the wrapper(s), we need to trigger the animation on the background rect
                bgRect.querySelector(`animate.${newState}`).beginElement();
                return;
            }

            // If the user clicks on an asset (rect) in the cluster marker
            if(e.target.classList.contains(ASSETS_CLUSTERS_CLASSES.ASSET_BACKGROUND_RECT)){

                // Deselct the previously selected asset
                document.querySelectorAll(`.${ASSETS_CLUSTERS_CLASSES.ASSET_BACKGROUND_RECT}.${ASSETS_CLUSTERS_CLASSES.SELECTED}`).forEach(el => {
                    
                    // parents
                    el.closest(`.${ASSETS_CLUSTERS_CLASSES.CLUSTER_MARKER}`).classList.remove(ASSETS_CLUSTERS_CLASSES.CONTAINS_SELECTED_ASSET);

                    // The rect
                    el.style.fill = ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_BACKGROUND;
                    el.classList.remove(ASSETS_CLUSTERS_CLASSES.SELECTED);

                    // The text
                    el.nextElementSibling.style.fill = ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR;
                    el.nextElementSibling.classList.remove(ASSETS_CLUSTERS_CLASSES .SELECTED);
                });

                // Select the clicked asset

                // parents
                markerElement.classList.add(ASSETS_CLUSTERS_CLASSES.CONTAINS_SELECTED_ASSET);

                // The rect
                e.target.style.fill = ASSETS_CLUSTERS_COLORS.SELECTED_ASSET_BACKGROUND;
                e.target.classList.add(ASSETS_CLUSTERS_CLASSES .SELECTED);

                // The text
                e.target.nextElementSibling.style.fill = ASSETS_CLUSTERS_COLORS.SELECTED_ASSET_COLOR;
                e.target.nextElementSibling.classList.add(ASSETS_CLUSTERS_CLASSES  .SELECTED);

                // Get the asset uuid and select it
                const assetUuid = e.target.dataset.uuid;
                this.props.onSelectResource('asset', assetUuid);
                
                return;
            }
        });

        el.addEventListener('wheel', (e) => {
            e.stopPropagation();
            e.preventDefault();
            
            // RAV-5521 Scrolling should happen only for an expanded cluster marker
            const shouldScroll = e.target.closest(`.cluster-marker`).querySelector(`rect.expanded`) !== null;
            if(shouldScroll){
                const innerWrapper = e.target.closest(`.${ASSETS_CLUSTERS_CLASSES.LIST_WRAPPER}`);
                if(innerWrapper)
                    innerWrapper.scrollTop += e.deltaY;
            }
        });
        
        el.classList.add(ASSETS_CLUSTERS_CLASSES.CLUSTER_MARKER);

        el.innerHTML = html;

        this.setClustermarkerLayout(el, props.anchorPosition);

        return el;
    }

    // code for creating an SVG donut chart from feature properties
    updateClusterMarkerElement(markerElement, props) {

        // asset(s) different heights
        const singleAssetHeight = ASSETS_CLUSTERS_FONT_SIZE;
        const singleAssetTotalHeight = singleAssetHeight + ASSETS_CLUSTERS_SINGLE_ASSET_PADDING * 2; // height + padding top and bottom
    
        
        // svg wrapping base and stem
        const baseSvgWidth = props.anchorPosition === ASSETS_CLUSTERS_ANCHORS.BOTTOM ? ASSETS_CLUSTERS_LIST_WIDTH : ASSETS_CLUSTERS_BASE_RADIUS * 2 + ASSETS_CLUSTERS_STEM_LONG_LENGTH;
        const baseSvgHeight = props.anchorPosition === ASSETS_CLUSTERS_ANCHORS.BOTTOM ? ASSETS_CLUSTERS_BASE_RADIUS * 2 + ASSETS_CLUSTERS_STEM_LENGTH : singleAssetTotalHeight;
        const baseSvgViewBox = `0 0 ${baseSvgWidth} ${baseSvgHeight}`;

        // circle
        const baseProps = this.getClusterBaseCircleProps(props, baseSvgHeight, baseSvgWidth);

        // line
        const stemProps = this.getClusterStemProps(props, baseSvgHeight, baseSvgWidth);

        const baseSVG = markerElement.querySelector('svg.base-svg');
        
        if(!baseSVG) return;

        baseSVG.setAttribute('width', baseSvgWidth);
        baseSVG.setAttribute('height', baseSvgHeight);
        baseSVG.setAttribute('viewBox', baseSvgViewBox);

        // circle at the top of the marker line
        const baseCircle = baseSVG.querySelector('circle');
        if(baseCircle){
            baseCircle.setAttribute('cx', baseProps.cx);
            baseCircle.setAttribute('cy', baseProps.cy);
            baseCircle.setAttribute('opacity', baseProps.opacity);
        }

        // vertical line connecting the base to the cluster popup
        const stemLine = baseSVG.querySelector('line');
        
        if(stemLine){
            stemLine.setAttribute('x1', stemProps.x1);
            stemLine.setAttribute('x2', stemProps.x2);
            stemLine.setAttribute('y1', stemProps.y1);
            stemLine.setAttribute('y2', stemProps.y2);
            stemLine.setAttribute('opacity', stemProps.opacity);
        }

        
        this.setClustermarkerLayout(markerElement, props.anchorPosition);

        return markerElement;
    }

    // code for creating an SVG donut chart from feature properties
    createClusterMarkerImage(feature) {
        console.log('createClusterMarkerImage', feature.id, feature.properties.containsTheSelectedResource, feature.properties.isSelected);
        const assetsInTheCluster = feature.properties.leaves;
        const highlightIt = feature.properties.containsTheSelectedResource;
        const showStemAndBase = !feature.properties.hasSubClusters;

        if(!assetsInTheCluster) return;

        const label = assetsInTheCluster.length;
        
        const expandable = assetsInTheCluster.length > ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST;


        // asset(s) different heights
        const singleAssetHeight = ASSETS_CLUSTERS_FONT_SIZE;
        const singleAssetTotalHeight = singleAssetHeight + ASSETS_CLUSTERS_SINGLE_ASSET_PADDING * 2; // height + padding top and bottom
        const allAssetsHeight = singleAssetTotalHeight * assetsInTheCluster.length;

        // list
        // const paddingsAndBorders = + ASSETS_CLUSTERS_LIST_BORDER_WIDTH * 2;
        const listHeight = singleAssetTotalHeight + ASSETS_CLUSTERS_LIST_PADDING * 2 + 2 // (assetsInTheCluster.length > ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST ? singleAssetTotalHeight : allAssetsHeight) + ASSETS_CLUSTERS_LIST_PADDING * 2 + 2; // If number of assets in the cluster is more than the max number to show, show only a title and a down arrow (which can be expanded)
        const listWidth = ASSETS_CLUSTERS_LIST_WIDTH; // width - border width

        // rect as list of assets background
        const backgroundRectBackground = highlightIt ? ASSETS_CLUSTERS_COLORS.SELECTED_CLUSTER_BACKGROUND : ASSETS_CLUSTERS_COLORS.DEFAULT_CLUSTER_BACKGROUND;
        const backgroundRectClass = expandable ? ASSETS_CLUSTERS_CLASSES.EXPANDABLE_LIST_BACKGROUND_RECT : ASSETS_CLUSTERS_CLASSES.NORMAL_LIST_BACKGROUND_RECT;        
        const backgroundRectY = ASSETS_CLUSTERS_LIST_BORDER_WIDTH;
        const backgroundRectX = ASSETS_CLUSTERS_LIST_BORDER_WIDTH;
        
        // list item rect
        const listItemX = 10;
        const listItemWidth = ASSETS_CLUSTERS_LIST_WIDTH - (listItemX * 2);
        
        // list item text
        const listItemTextX = listItemX * 2;

        // expand/collapse arrow
        const arrowSideLength = 5;
        const arrowStartingX = ASSETS_CLUSTERS_LIST_WIDTH - (listItemTextX + arrowSideLength * 2);
        const arrowStartingY = ASSETS_CLUSTERS_LIST_PADDING / 2;
        const arrowPath = `M${arrowStartingX} Y l${arrowSideLength} ${arrowSideLength} l${arrowSideLength} -${arrowSideLength}`;


        // list item separator
        const listItemSeparatorX1 = listItemX;
        const listItemSeparatorX2 = ASSETS_CLUSTERS_LIST_WIDTH - listItemX;
        const listItemSeparatorY = singleAssetHeight + ASSETS_CLUSTERS_SINGLE_ASSET_PADDING - 1;
        
        // base and stem
        const baseHeight = showStemAndBase ? ASSETS_CLUSTERS_BASE_RADIUS * 2 + ASSETS_CLUSTERS_STEM_LENGTH : 0;
        
        //  wrapping svg
        const svgWidth = ASSETS_CLUSTERS_LIST_WIDTH + ASSETS_CLUSTERS_LIST_BORDER_WIDTH * 2;
        const svgHeight = listHeight + baseHeight + ASSETS_CLUSTERS_LIST_BORDER_WIDTH * 2;
        const svgViewBox = `0 0 ${svgWidth} ${svgHeight}`;


        // circle
        const baseProps = {
            cx: '50%',
            cy: svgHeight - ASSETS_CLUSTERS_BASE_RADIUS,
            opacity: 1
        };

        // line
        const stemProps = {
            x1: '50%',
            x2:  '50%',
            y1: listHeight + ASSETS_CLUSTERS_LIST_BORDER_WIDTH,
            y2: svgHeight - ASSETS_CLUSTERS_BASE_RADIUS,
            opacity: 1
        };        
        


        // head Part of the marker (list of assets)        
        let content = `<svg  xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewbox="${svgViewBox}" text-anchor="middle" font-size="${ASSETS_CLUSTERS_FONT_SIZE}" font-family="'Helvetica Neue', Helvetica, Arial, 'sans-serif'">`;
            content += `<g>`;
                // rect as cluster popup background
                content += `<rect class="${backgroundRectClass}" x="${backgroundRectX}" y="${backgroundRectY}" width="${listWidth}" height="${listHeight}" rx="15" ry="15" fill="${backgroundRectBackground}" stroke="${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER}" stroke-width="${ASSETS_CLUSTERS_LIST_BORDER_WIDTH}" />`;


                let y = ASSETS_CLUSTERS_LIST_PADDING + ASSETS_CLUSTERS_SINGLE_ASSET_PADDING + ASSETS_CLUSTERS_LIST_BORDER_WIDTH + 1;


                // Note: For cluster image, it's better to always just show the title and the down arrow
                // Because if we show expanded list as image and then user clicks on an image thinking they are selecting a single asset in the list, we can't detect which asset they clicked on
                
                // add an down arrow (not a triangle) to the right of the donut chart
                // if(assetsInTheCluster.length > ASSETS_CLUSTERS_MAX_POINT_TO_SHOW_LIST){
                    content += `<text dominant-baseline="hanging" text-anchor="start" x="${listItemTextX}" y="${y}" fill="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR}">${label} ${strings.resource_group_title_assets}</text>`;
                    content += `<path d="${arrowPath.replace('Y', y + arrowStartingY)}" stroke="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR}" fill="transparent" stroke-width="1"></path>`;
                    
                    y += ASSETS_CLUSTERS_LIST_PADDING + singleAssetTotalHeight + 1;
                // }
                // else{
                //     for (let i = 0; i < assetsInTheCluster.length; i++) {
                //         const asset = assetsInTheCluster[i];
                //         const assetName = asset.properties.assetName;
                //         const assetUuid = asset.properties.ravenUuid;

                //         content += `<rect x="${listItemX}" y="${y - ASSETS_CLUSTERS_SINGLE_ASSET_PADDING - 1}" width="${listItemWidth}" height="${singleAssetTotalHeight}" fill="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_BACKGROUND}" />`;
                //         content += `<text dominant-baseline="hanging" text-anchor="start" x="${listItemTextX}" y="${y}" fill="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR}">${assetName}</text>`;
                        
                //         // add a horizontal line to separate assets
                //         if(i < assetsInTheCluster.length - 1){
                //             content += `<line x1="${listItemSeparatorX1}" y1="${y + listItemSeparatorY}" x2="${listItemSeparatorX2}" y2="${y + listItemSeparatorY}" stroke="${ASSETS_CLUSTERS_COLORS.DEFAULT_ASSET_COLOR}" stroke-width="1" stroke-opacity="30%"></line>`;
                //         }

                //         y += singleAssetTotalHeight;
                //     }
                // }

            content += `</g>`;

            // lower Part of the marker (base and stem)
            if(showStemAndBase){
                content += `<g>`;
                    // circle at the top of the marker line
                    content += `<circle cx="${baseProps.cx}" cy="${baseProps.cy}" opacity="${baseProps.opacity}" r="${ASSETS_CLUSTERS_BASE_RADIUS}" fill="${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER}"></circle>`;
                
                    // vertical line connecting the base to the cluster popup
                    content += `<line x1="${stemProps.x1}" y1="${stemProps.y1}" x2="${stemProps.x2}" y2="${stemProps.y2}" opacity="${stemProps.opacity}" stroke="${ASSETS_CLUSTERS_COLORS.CLUSTER_BORDER}" stroke-width="2"></line>`;
                content += `</g>`;
            }

        content += `</svg>`;
                


        return content;
    }

    getClusterBaseCircleProps = (clusterProps, svgHeight, svgWidth) => {
        const { isCoveringAPin, status } = clusterProps;

        const props = {
            cx: '50%',
            cy: svgHeight - ASSETS_CLUSTERS_BASE_RADIUS,
            opacity: 1
        }

        if(!isCoveringAPin){
            return props;
        } 

        const { position, distance } = status;

        
        // If the cluster base circle is on the head of a pin, hide it
        if(position.vertically === ASSETS_CLUSTERS_POSITION.ABOVE){
            if(this.isCoveringThePinHead(distance)){
                props.opacity = 0;
                return props;
            }
        }

        // Reaching here means we need to rotate the cluster to left or right
        props.cy = '50%';

        if(position.horizontally === ASSETS_CLUSTERS_POSITION.RIGHT){
            props.cx = ASSETS_CLUSTERS_BASE_RADIUS;
        }

        if(position.horizontally === ASSETS_CLUSTERS_POSITION.LEFT){
            props.cx = svgWidth - ASSETS_CLUSTERS_BASE_RADIUS;
        }

        // If the cluster base circle is on the stem/base of a pin, hide it
        if(position.horizontally === ASSETS_CLUSTERS_POSITION.ALIGNED){
            if(this.isCoveringPinStem(distance)){ 
                props.opacity = 0;
            }
        }


        return props;
    }

    getClusterStemProps = (clusterProps, svgHeight, svgWidth) => {
        const { isCoveringAPin, status } = clusterProps;

        // default is a vertical line
        const props = {
            x1: '50%',
            x2:  '50%',
            y1: 0,
            y2: svgHeight - ASSETS_CLUSTERS_BASE_RADIUS,
            opacity: 1
        }

        if(!isCoveringAPin){
            return props;
        } 

        const { position, distance } = status;

        // If the cluster base circle is on the head of a pin, we need to cut the stem and show a shorter stem
        if(position.vertically === ASSETS_CLUSTERS_POSITION.ABOVE){
            if(this.isCoveringThePinHead(distance)){
                props.y2 = svgHeight - (49 - distance.vertically); // Although height of Pin is 52, 49 gives the best result for cutting the stem
                return props;
            }
        }

        props.y1 = '50%';
        props.y2 = '50%';

        // If covering a pin, make it a horizontal line

        if(position.horizontally === ASSETS_CLUSTERS_POSITION.RIGHT){
            props.x1 = ASSETS_CLUSTERS_BASE_RADIUS;
            props.x2 = svgWidth;
        }

        if(position.horizontally === ASSETS_CLUSTERS_POSITION.LEFT){
            props.x1 = 0;
            props.x2 = svgWidth - ASSETS_CLUSTERS_BASE_RADIUS;
        }

        return props;
    }

    getClusterMarkerAnchor = (isCoveringAPin, status) => {
        if(!isCoveringAPin){
            return ASSETS_CLUSTERS_ANCHORS.BOTTOM;
        } 

        const { position, distance } = status;

        if(position.vertically === ASSETS_CLUSTERS_POSITION.ABOVE){
            if(this.isCoveringThePinHead(distance)){
                return ASSETS_CLUSTERS_ANCHORS.BOTTOM;
            }
        }

        if(position.horizontally === ASSETS_CLUSTERS_POSITION.RIGHT){
            return ASSETS_CLUSTERS_ANCHORS.LEFT;
        }

        if(position.horizontally === ASSETS_CLUSTERS_POSITION.LEFT){
            return ASSETS_CLUSTERS_ANCHORS.RIGHT;
        }

        return ASSETS_CLUSTERS_ANCHORS.LEFT;
    }

    setClustermarkerLayout = (element, anchor) => {
        element.style.display = 'flex';
        element.style.justifyContent = 'center';
        element.style.alignItems = 'center';

        switch(true){
            case anchor === ASSETS_CLUSTERS_ANCHORS.BOTTOM:
                element.style.flexDirection = 'column';
                break;
            case anchor === ASSETS_CLUSTERS_ANCHORS.LEFT:
                element.style.flexDirection = 'row-reverse';
                break;
            case anchor === ASSETS_CLUSTERS_ANCHORS.RIGHT:
                element.style.flexDirection = 'row';
                break;
        }
    }

    isCoveringThePinHead = (distance) => {
        return (distance.vertically > 30 && distance.vertically <= 54) && distance.horizontally < 80; // being within these ranges means the cluster is covering the head of the pin (few pixels are added for certainty : 30 and 54 instead of 34 and 52)
    }

    isCoveringPinStem = (distance) => {
        return distance.vertically >= 0 && distance.vertically <= 34; // 34 is the length of vehicle pin's stem + base (check mapboxhelperdashboard.js line 581)
    }
}

DashboardMap.propTypes = {
    // Basic configuration
    mapId: PropTypes.string.isRequired,
    theme: PropTypes.oneOf(['light', 'dark']),
    stage: PropTypes.string.isRequired,
    
    // User and permissions
    user: PropTypes.shape({
        stage: PropTypes.string
    }),
    canAccessAssets: PropTypes.bool,
    
    // Features and flags
    features: PropTypes.object,
    unitOfMeasure: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    
    // Data arrays
    ravens: PropTypes.arrayOf(PropTypes.object),
    assets: PropTypes.arrayOf(PropTypes.object),
    activeGeofenceIds: PropTypes.array,
    
    // Selection and UI state
    selectedResource: PropTypes.shape({
        resourceType: PropTypes.string,
        resourceId: PropTypes.string
    }),
    detailsPanelIsOpen: PropTypes.bool,
    geofenceGeometryEditModeActive: PropTypes.bool,
    mapTrailsVisible: PropTypes.bool,
    mainNavigationActiveMenuItemId: PropTypes.string,
    showcaseTargetRavenUuid: PropTypes.string,
    updateKey: PropTypes.any,
    
    // Functions - data loading
    loadActiveGeofences: PropTypes.func.isRequired,
    loadAllAssets: PropTypes.func.isRequired,
    loadAllBeacons: PropTypes.func.isRequired,
    
    // Functions - interactions
    onSelectResource: PropTypes.func.isRequired,
    onLiveStream: PropTypes.func.isRequired,
    
    // Functions - UI updates
    setLayoutFullscreen: PropTypes.func.isRequired,
    setMapTrailsVisible: PropTypes.func.isRequired,
    
    // Functions - permissions and checks
    canSendDriverMessage: PropTypes.func.isRequired,
    
    // Functions - geofence related
    isGeofenceVisible: PropTypes.func.isRequired,
    geofenceName: PropTypes.func.isRequired,
    geofenceGeojson: PropTypes.func.isRequired,
    geofenceColor: PropTypes.func.isRequired,
    geofenceType: PropTypes.func.isRequired
};